import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, doc, getDoc, deleteDoc, updateDoc, addDoc, serverTimestamp, query, where, onSnapshot } from "firebase/firestore";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import app from "./firebase";
import { useCookies } from "react-cookie";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import {
  Leaf, Users, AlertCircle, MessageSquare, Lock, Trash2, ChevronRight,
  LogOut, Menu, X, Search, CheckCircle2, Clock, XCircle, Send, Eye, EyeOff, Package, Plus, Edit2, TrendingUp, Layers,
  Bot, Globe, Phone, Mail, User, ShoppingBag, RefreshCw, Sparkles, ExternalLink, Tag, Award, UserCheck
} from "lucide-react";

const db = getFirestore(app);
const storage = getStorage(app);
const ADMIN_ID = "admin"; // Change this to actual admin user ID(s)

export default function AdminDashboard() {
  const [cookies, , removeCookie] = useCookies(["username", "name"]);
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allInvestments, setAllInvestments] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("users"); // users, tickets, inquiries, products, investments, categories, ai_agent, central_market, agents
  const [allMarketplaceLeads, setAllMarketplaceLeads] = useState([]);
  const [allAgentRequests, setAllAgentRequests] = useState([]);
  const [allInquiries, setAllInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedAgentRequest, setSelectedAgentRequest] = useState(null);
  const [agentRejectComment, setAgentRejectComment] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [ticketReply, setTicketReply] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [resolution, setResolution] = useState("");
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", amount: "", measurment: "", category: "" });
  const [categoryForm, setCategoryForm] = useState({ name: "" });
  const [toast, setToast] = useState(null);
  const [editingInvestmentCategory, setEditingInvestmentCategory] = useState(false);
  const [investmentCategoryInput, setInvestmentCategoryInput] = useState("");
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [productCategorySuggestions, setProductCategorySuggestions] = useState(false);
  const [adminWallet, setAdminWallet] = useState(0);
  const [transferForm, setTransferForm] = useState({ fromEmail: "", toEmail: "", amount: "" });
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [revokeReason, setRevokeReason] = useState("");
  const [reassignForm, setReassignForm] = useState({ fromAgentEmail: "", toAgentEmail: "" });
  const [fromAgentSug, setFromAgentSug] = useState([]);
  const [toAgentSug, setToAgentSug] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", content: "", image: "", author: "Admin" });
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [isEditingBlog, setIsEditingBlog] = useState(false);
  const [blogComments, setBlogComments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // ── AI Agent Search State ──
  const [agentResults, setAgentResults] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [pushedIds, setPushedIds] = useState(new Set());
  const [agentSearchCount, setAgentSearchCount] = useState(0);
  // cardEdits: { [_id]: { price: "", description: "" } }
  const [cardEdits, setCardEdits] = useState({});

  // Derive all unique categories (Manual + from Products)
  const availableCategories = React.useMemo(() => {
    const list = new Set();
    allCategories.forEach(c => c.name && list.add(c.name));
    allProducts.forEach(p => p.category && list.add(p.category));
    return Array.from(list).sort((a, b) => a.localeCompare(b));
  }, [allCategories, allProducts]);

  // Verify admin access
  useEffect(() => {
    if (cookies.username !== ADMIN_ID && cookies.username !== "obajemusagmailcom") {
      window.location.href = "/login";
    }
  }, [cookies.username]);

  useEffect(() => {
    if (show_mobile_menu) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu]);

  // Fetch all users and tickets
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users
        const usersSnapshot = await getDocs(collection(db, "users"));
        const users = usersSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => (b.wallet || 0) - (a.wallet || 0));
        setAllUsers(users);

        // Fetch tickets
        const ticketsSnapshot = await getDocs(collection(db, "admin_tickets"));
        const tickets = ticketsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => new Date(b.timestamp?.toDate?.() || b.timestamp) - new Date(a.timestamp?.toDate?.() || a.timestamp));
        setAllTickets(tickets);

        // Fetch products
        const productsSnapshot = await getDocs(collection(db, "products"));
        const products = productsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setAllProducts(products);

        // Fetch investments
        const investmentsSnapshot = await getDocs(collection(db, "investment"));
        const investments = investmentsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllInvestments(investments);

        // Fetch categories
        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categories = categoriesSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        setAllCategories(categories);

        // Fetch marketplace leads
        const leadsSnapshot = await getDocs(collection(db, "buyers_request"));
        const leads = leadsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => {
          // Attempt to sort by date string
          try {
            return new Date(b.time?.split('/').reverse().join('-')) - new Date(a.time?.split('/').reverse().join('-'));
          } catch {
            return 0;
          }
        });
        setAllMarketplaceLeads(leads);

        // Fetch agent requests
        const agentRequestsSnapshot = await getDocs(collection(db, "agent_requests"));
        const agentRequests = agentRequestsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setAllAgentRequests(agentRequests);

        // Fetch contact us inquiries
        const inquiriesSnapshot = await getDocs(collection(db, "admin_tickets"));
        const inquiries = inquiriesSnapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id }))
          .filter(t => t.type === "contact_us")
          .sort((a, b) => new Date(b.timestamp?.toDate?.() || b.timestamp) - new Date(a.timestamp?.toDate?.() || a.timestamp));
        setAllInquiries(inquiries);

        // Fetch Admin Wallet
        const adminSnap = await getDoc(doc(db, "users", "admin"));
        if (adminSnap.exists()) {
          setAdminWallet(adminSnap.data().wallet || 0);
        }

        // Fetch blogs
        const blogsSnapshot = await getDocs(collection(db, "blogs"));
        const blogs = blogsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })).sort((a, b) => new Date(b.timestamp?.toDate?.() || b.timestamp) - new Date(a.timestamp?.toDate?.() || a.timestamp));
        setAllBlogs(blogs);

      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("error", "Error", "Failed to load data");
      } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedBlog) {
      setBlogComments([]);
      return;
    }
    const fetchComments = async () => {
      try {
        const q = query(collection(db, "blog_comments"), where("blogId", "==", selectedBlog.id));
        const snap = await getDocs(q);
        setBlogComments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) { console.error(error); }
    };
    fetchComments();
  }, [selectedBlog]);

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDeleteCentralMarketLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this central market request?")) return;
    try {
      await deleteDoc(doc(db, "buyers_request", leadId));
      setAllMarketplaceLeads(allMarketplaceLeads.filter(l => l.id !== leadId));
      showToast("success", "Deleted", "Central market request removed successfully");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete request");
    }
  };

  const handeDeleteMarketplaceLead = handleDeleteCentralMarketLead; // Compatibility

  const handleFreezeAccount = async (userId, shouldFreeze) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        accountStatus: shouldFreeze ? "frozen" : "active"
      });
      // Update local state
      setAllUsers(allUsers.map(u => u.id === userId ? { ...u, accountStatus: shouldFreeze ? "frozen" : "active" } : u));
      setSelectedUser(selectedUser?.id === userId ? { ...selectedUser, accountStatus: shouldFreeze ? "frozen" : "active" } : selectedUser);
      showToast("success", "Success", `Account ${shouldFreeze ? "frozen" : "unfrozen"}`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to update account");
    }
  };

  const handleDeleteAccount = async (userId) => {
    if (!window.confirm("Are you sure you want to permanently delete this account? This cannot be undone.")) return;
    try {
      await updateDoc(doc(db, "users", userId), {
        accountStatus: "deleted",
        wallet: 0
      });
      setAllUsers(allUsers.filter(u => u.id !== userId));
      setSelectedUser(null);
      showToast("success", "Success", "Account deleted");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete account");
    }
  };

  const handleReplyTicket = async () => {
    if (!ticketReply.trim()) return;
    try {
      const updatedMessages = [...(selectedTicket.messages || []), {
        sender: "admin",
        senderName: "Admin Support",
        message: ticketReply,
        timestamp: new Date().toISOString()
      }];

      await updateDoc(doc(db, "admin_tickets", selectedTicket.id), {
        messages: updatedMessages,
        status: ticketStatus || selectedTicket.status,
        lastReplyTime: serverTimestamp()
      });

      // Update local state
      const updatedTicket = { ...selectedTicket, messages: updatedMessages, status: ticketStatus || selectedTicket.status };
      setSelectedTicket(updatedTicket);
      setAllTickets(allTickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setTicketReply("");
      showToast("success", "Success", "Reply sent");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to send reply");
    }
  };

  const handleResolveTicket = async () => {
    if (!resolution.trim()) return;
    try {
      const updatedMessages = [...(selectedTicket.messages || []), {
        sender: "admin",
        senderName: "Admin Support",
        message: "RESOLUTION: " + resolution,
        timestamp: new Date().toISOString()
      }];

      await updateDoc(doc(db, "admin_tickets", selectedTicket.id), {
        messages: updatedMessages,
        status: "resolved",
        resolution: resolution,
        resolvedAt: serverTimestamp()
      });

      const updatedTicket = { ...selectedTicket, messages: updatedMessages, status: "resolved", resolution };
      setSelectedTicket(updatedTicket);
      setAllTickets(allTickets.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      setResolution("");
      showToast("success", "Success", "Ticket resolved");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to resolve ticket");
    }
  };

  const handleCreateProduct = async () => {
    if (!productForm.name.trim() || !productForm.amount.trim() || !productForm.measurment.trim() || !productForm.category) {
      showToast("error", "Validation", "All fields are required");
      return;
    }
    try {
      await addDoc(collection(db, "products"), {
        name: productForm.name,
        amount: productForm.amount,
        measurment: productForm.measurment,
        category: productForm.category,
        createdAt: serverTimestamp()
      });

      // Refresh products
      const productsSnapshot = await getDocs(collection(db, "products"));
      const products = productsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setAllProducts(products);

      setProductForm({ name: "", amount: "", measurment: "", category: "" });
      setShowProductModal(false);
      showToast("success", "Success", "Product created");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to create product");
    }
  };

  const handleUpdateProduct = async () => {
    if (!productForm.name.trim() || !productForm.amount.trim() || !productForm.measurment.trim() || !productForm.category) {
      showToast("error", "Validation", "All fields are required");
      return;
    }
    try {
      await updateDoc(doc(db, "products", selectedProduct.id), {
        name: productForm.name,
        amount: productForm.amount,
        measurment: productForm.measurment,
        category: productForm.category
      });

      // --- Global Investment Update & Notifications ---
      const oldPrice = selectedProduct.amount;
      const newPrice = productForm.amount;

      if (oldPrice !== newPrice) {
        try {
          // 1. Get all active investments for this product
          const invQuery = query(collection(db, "investment"), where("name", "==", selectedProduct.name), where("investment_status", "==", " active"));
          const invSnap = await getDocs(invQuery);

          const affectedUsers = new Set();

          const updatePromises = invSnap.docs.map(async (invDoc) => {
            const invData = invDoc.data();
            affectedUsers.add(invData.owner);
            return updateDoc(doc(db, "investment", invDoc.id), { price: newPrice });
          });

          await Promise.all(updatePromises);

          // 2. Notify affected users
          affectedUsers.forEach(async (uId) => {
            try {
              const uSnap = await getDoc(doc(db, "users", uId));
              if (!uSnap.exists()) return;
              const uData = uSnap.data();
              const uEmail = uData.email;
              if (!uEmail) return;

              const notifSnap = await getDoc(doc(db, "notification", uId));
              if (notifSnap.exists() && notifSnap.data().price_change) {
                const templateParams = {
                  header: "AGRIVESTIA INVESTMENT",
                  subject: "PRICE UPDATE ALERT",
                  message: `Hi ${uData.name}, the market price for "${selectedProduct.name}" has been updated to ₦${newPrice}. This change has been automatically applied to your active investments.`,
                  email: uEmail
                };
                await emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd");
              }
            } catch (err) { console.error("Notification error:", err); }
          });
        } catch (err) { console.error("Global update error:", err); }
      }
      // ------------------------------------------------

      const updatedProduct = {
        ...selectedProduct,
        name: productForm.name,
        amount: productForm.amount,
        measurment: productForm.measurment,
        category: productForm.category
      };

      setSelectedProduct(updatedProduct);
      setAllProducts(allProducts.map(p => p.id === selectedProduct.id ? updatedProduct : p));
      setProductForm({ name: "", amount: "", measurment: "", category: "" });
      setShowProductModal(false);
      setIsEditingProduct(false);
      showToast("success", "Success", "Product updated and reflected in active investments.");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to update product");
    }
  };

  const handleDeleteProduct = async () => {
    if (!window.confirm("Are you sure you want to delete this product? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "products", selectedProduct.id));
      setAllProducts(allProducts.filter(p => p.id !== selectedProduct.id));
      setSelectedProduct(null);
      showToast("success", "Success", "Product deleted");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete product");
    }
  };

  const handleDeleteInvestment = async () => {
    if (!window.confirm("Are you sure you want to delete this investment? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "investment", selectedInvestment.id));
      setAllInvestments(allInvestments.filter(i => i.id !== selectedInvestment.id));
      setSelectedInvestment(null);
      showToast("success", "Success", "Investment deleted");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete investment");
    }
  };

  const handleUpdateInvestmentCategory = async (newCategory) => {
    if (!newCategory.trim()) {
      showToast("error", "Error", "Category cannot be empty");
      return;
    }
    try {
      await updateDoc(doc(db, "investment", selectedInvestment.id), {
        category: newCategory
      });
      const updatedInvestment = { ...selectedInvestment, category: newCategory };
      setSelectedInvestment(updatedInvestment);
      setAllInvestments(allInvestments.map(i => i.id === selectedInvestment.id ? updatedInvestment : i));
      setEditingInvestmentCategory(false);
      setInvestmentCategoryInput("");
      showToast("success", "Success", "Investment category updated");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to update category");
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      showToast("error", "Validation", "Category name is required");
      return;
    }
    try {
      await addDoc(collection(db, "categories"), {
        name: categoryForm.name,
        createdAt: serverTimestamp()
      });

      // Refresh categories
      const categoriesSnapshot = await getDocs(collection(db, "categories"));
      const categories = categoriesSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setAllCategories(categories);

      setCategoryForm({ name: "" });
      setShowCategoryModal(false);
      showToast("success", "Success", "Category created");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to create category");
    }
  };

  const handleUpdateCategory = async () => {
    if (!categoryForm.name.trim()) {
      showToast("error", "Validation", "Category name is required");
      return;
    }
    try {
      await updateDoc(doc(db, "categories", selectedCategory.id), {
        name: categoryForm.name
      });

      const updatedCategory = { ...selectedCategory, name: categoryForm.name };
      setSelectedCategory(updatedCategory);
      setAllCategories(allCategories.map(c => c.id === selectedCategory.id ? updatedCategory : c));
      setCategoryForm({ name: "" });
      setShowCategoryModal(false);
      setIsEditingCategory(false);
      showToast("success", "Success", "Category updated");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to update category");
    }
  };

  const handleDeleteCategory = async () => {
    if (!window.confirm("Are you sure you want to delete this category? This action cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "categories", selectedCategory.id));
      setAllCategories(allCategories.filter(c => c.id !== selectedCategory.id));
      setSelectedCategory(null);
      showToast("success", "Success", "Category deleted");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to delete category");
    }
  };

  const handleApproveAgent = async (request) => {
    try {
      // Generate a unique Agent ID (e.g., AGV-1234)
      const randomId = Math.floor(1000 + Math.random() * 9000);
      const agentId = `AGV-${randomId}`;

      // 1. Update user document
      await updateDoc(doc(db, "users", request.userId), {
        isAgent: true,
        agentStatus: "approved",
        agentId: agentId
      });

      // 2. Remove from requests collection
      await deleteDoc(doc(db, "agent_requests", request.userId));

      // 3. Update local state
      setAllAgentRequests(allAgentRequests.filter(r => r.userId !== request.userId));
      setSelectedAgentRequest(null);
      showToast("success", "Agent Approved", `${request.userName} is now an official agent (ID: ${agentId})`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to approve agent request");
    }
  };

  const handleRejectAgent = async (request) => {
    if (!agentRejectComment.trim()) {
      showToast("error", "Required", "Please provide a reason for rejection");
      return;
    }
    try {
      // 1. Update user document
      await updateDoc(doc(db, "users", request.userId), {
        agentStatus: "rejected",
        agentRejectReason: agentRejectComment
      });

      // 2. Remove from requests collection
      await deleteDoc(doc(db, "agent_requests", request.userId));

      // 3. Update local state
      setAllAgentRequests(allAgentRequests.filter(r => r.userId !== request.userId));
      setSelectedAgentRequest(null);
      setAgentRejectComment("");
      showToast("success", "Agent Rejected", "Request has been declined with feedback");
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to reject agent request");
    }
  };

  const handleAdminPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0 || amount > adminWallet) {
      showToast("error", "Invalid Amount", "Please enter a valid amount within balance.");
      return;
    }
    if (!window.confirm(`Process payout of ₦${amount.toLocaleString()}? This will deduct from the platform wallet.`)) return;

    try {
      const newBalance = adminWallet - amount;
      await updateDoc(doc(db, "users", "admin"), { wallet: newBalance });
      setAdminWallet(newBalance);
      setPayoutAmount("");
      showToast("success", "Payout Recorded", `₦${amount.toLocaleString()} has been deducted from platform wallet.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to process payout");
    }
  };

  const handleUserToUserTransfer = async () => {
    const { fromEmail, toEmail, amount: amtStr } = transferForm;
    const amount = parseFloat(amtStr);

    if (!fromEmail || !toEmail || isNaN(amount) || amount <= 0) {
      showToast("error", "Invalid Input", "Please fill all fields with valid data.");
      return;
    }

    if (!window.confirm(`Transfer ₦${amount.toLocaleString()} from ${fromEmail} to ${toEmail}?`)) return;

    try {
      // Find users by email
      const usersRef = collection(db, "users");
      const qFrom = query(usersRef, where("email", "==", fromEmail.trim()));
      const qTo = query(usersRef, where("email", "==", toEmail.trim()));

      const fromSnap = await getDocs(qFrom);
      const toSnap = await getDocs(qTo);

      if (fromSnap.empty || toSnap.empty) {
        showToast("error", "User Not Found", "One or both email addresses do not match any user.");
        return;
      }

      const fromDoc = fromSnap.docs[0];
      const toDoc = toSnap.docs[0];
      const fromId = fromDoc.id;
      const toId = toDoc.id;

      const fromWallet = fromDoc.data().wallet || 0;
      const toWallet = toDoc.data().wallet || 0;

      if (fromWallet < amount) {
        showToast("error", "Insufficient Funds", `Source user only has ₦${fromWallet.toLocaleString()}.`);
        return;
      }

      await updateDoc(doc(db, "users", fromId), { wallet: fromWallet - amount });
      await updateDoc(doc(db, "users", toId), { wallet: toWallet + amount });

      // Update admin wallet state if involved (using ID 'admin')
      if (fromId === "admin") setAdminWallet(fromWallet - amount);
      if (toId === "admin") setAdminWallet(toWallet + amount);

      // Refresh local users list state
      setAllUsers(allUsers.map(u => {
        if (u.id === fromId) return { ...u, wallet: fromWallet - amount };
        if (u.id === toId) return { ...u, wallet: toWallet + amount };
        return u;
      }));

      setTransferForm({ fromEmail: "", toEmail: "", amount: "" });
      showToast("success", "Transfer Successful", `₦${amount.toLocaleString()} moved correctly.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to complete transfer.");
    }
  };

  const handleRevokeAgent = async (user) => {
    if (!revokeReason.trim()) {
      showToast("error", "Reason Required", "Please provide a reason for revocation.");
      return;
    }
    if (!window.confirm(`Revoke agent license for ${user.name}?`)) return;

    try {
      await updateDoc(doc(db, "users", user.id), {
        isAgent: false,
        agentStatus: "revoked",
        agentRevokeReason: revokeReason
      });

      setAllUsers(allUsers.map(u => {
        if (u.id === user.id) return { ...u, isAgent: false, agentStatus: "revoked", agentRevokeReason: revokeReason };
        return u;
      }));

      setSelectedUser({ ...user, isAgent: false, agentStatus: "revoked", agentRevokeReason: revokeReason });
      setRevokeReason("");
      showToast("success", "Agent Revoked", `${user.name} is no longer an agent.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to revoke agent.");
    }
  };

  const handleReassignClients = async () => {
    const { fromAgentEmail, toAgentEmail } = reassignForm;
    if (!fromAgentEmail || !toAgentEmail) {
      showToast("error", "Missing Info", "Please provide both agent emails.");
      return;
    }

    try {
      const usersRef = collection(db, "users");
      const qFrom = query(usersRef, where("email", "==", fromAgentEmail.trim()));
      const qTo = query(usersRef, where("email", "==", toAgentEmail.trim()));

      const fromSnap = await getDocs(qFrom);
      const toSnap = await getDocs(qTo);

      if (fromSnap.empty || toSnap.empty) {
        showToast("error", "Agent Not Found", "One or both agent emails do not exist.");
        return;
      }

      const fromAgent = fromSnap.docs[0].data();
      const toAgentDoc = toSnap.docs[0];
      const toAgent = toAgentDoc.data();

      if (!fromAgent.isAgent && fromAgent.agentStatus !== "revoked") {
        showToast("error", "Invalid Source", "The source user is not an agent.");
        return;
      }

      const clientsQuery = query(usersRef, where("managedBy", "==", fromSnap.docs[0].id));
      const clientsSnap = await getDocs(clientsQuery);

      if (clientsSnap.empty) {
        showToast("info", "No Clients", "This agent has no managed clients.");
        return;
      }

      if (!window.confirm(`Move ${clientsSnap.size} clients to ${toAgent.name}?`)) return;

      const batchPromises = clientsSnap.docs.map(clientDoc =>
        updateDoc(doc(db, "users", clientDoc.id), {
          managedBy: toAgentDoc.id,
          managedByName: toAgent.name,
          managedByEmail: toAgent.email
        })
      );

      await Promise.all(batchPromises);

      setReassignForm({ fromAgentEmail: "", toAgentEmail: "" });
      showToast("success", "Reassignment Complete", `Moved ${clientsSnap.size} clients successfully.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to reassign clients.");
    }
  };

  const handleToggleAgentActive = async (agent, newActive) => {
    const status = newActive ? "approved" : "disabled";
    if (!window.confirm(`${newActive ? "Enable" : "Disable"} agent capabilities for ${agent.name}?`)) return;

    try {
      await updateDoc(doc(db, "users", agent.id), { agentStatus: status });
      setAllUsers(allUsers.map(u => u.id === agent.id ? { ...u, agentStatus: status } : u));
      if (selectedUser?.id === agent.id) setSelectedUser({ ...selectedUser, agentStatus: status });
      if (selectedAgent?.id === agent.id) setSelectedAgent({ ...selectedAgent, agentStatus: status });
      showToast("success", "Status Updated", `Agent status is now ${status}.`);
    } catch (error) {
      console.error(error);
      showToast("error", "Error", "Failed to update agent status.");
    }
  };

  const handleToggleAccountStatus = async (user, newStatus) => {
    if (!window.confirm(`Set account status for ${user.name} to ${newStatus.toUpperCase()}?`)) return;
    try {
      await updateDoc(doc(db, "users", user.id), { accountStatus: newStatus });
      setAllUsers(allUsers.map(u => u.id === user.id ? { ...u, accountStatus: newStatus } : u));
      setSelectedUser(selectedUser?.id === user.id ? { ...selectedUser, accountStatus: newStatus } : selectedUser);
      showToast("success", "Success", `Account ${newStatus}`);
    } catch (error) { console.error(error); showToast("error", "Error", "Failed to update status"); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    const storageRef = ref(storage, `blog_images/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (error) => {
        console.error("Upload error:", error);
        setIsUploading(false);
        showToast("error", "Upload Failed", "Could not upload image. Please try again.");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setBlogForm(prev => ({ ...prev, image: downloadURL }));
          setIsUploading(false);
          setUploadProgress(0);
          showToast("success", "Uploaded", "Image uploaded successfully!");
        });
      }
    );
  };

  const handleCreateBlog = async () => {
    if (!blogForm.title || !blogForm.content) {
      showToast("error", "Error", "Please fill in all fields");
      return;
    }
    try {
      const blogData = {
        ...blogForm,
        image: blogForm.image || "",
        timestamp: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, "blogs"), blogData);
      const newBlog = { id: docRef.id, ...blogData, timestamp: new Date() };
      setAllBlogs([newBlog, ...allBlogs]);
      setShowBlogModal(false);
      setBlogForm({ title: "", content: "", image: "", author: "Admin" });
      showToast("success", "Success", "Blog post created");
    } catch (error) { console.error(error); showToast("error", "Error", "Failed to create post"); }
  };

  const handleUpdateBlog = async () => {
    if (!blogForm.title || !blogForm.content) return;
    try {
      await updateDoc(doc(db, "blogs", selectedBlog.id), {
        ...blogForm,
        lastUpdated: serverTimestamp()
      });
      setAllBlogs(allBlogs.map(b => b.id === selectedBlog.id ? { ...b, ...blogForm } : b));
      setShowBlogModal(false);
      setSelectedBlog({ ...selectedBlog, ...blogForm });
      showToast("success", "Success", "Blog post updated");
    } catch (error) { console.error(error); showToast("error", "Error", "Failed to update post"); }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await deleteDoc(doc(db, "blogs", blogId));
      setAllBlogs(allBlogs.filter(b => b.id !== blogId));
      setSelectedBlog(null);
      showToast("success", "Deleted", "Blog post removed");
    } catch (error) { console.error(error); showToast("error", "Error", "Failed to delete post"); }
  };

  const isFrozen = selectedUser?.accountStatus === "frozen";
  const filteredUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.id.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredTickets = allTickets.filter(t =>
    (t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.type?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    t.type !== "contact_us"
  );
  const filteredInquiries = allTickets.filter(t =>
    t.type === "contact_us" &&
    (t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.createdByName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.createdByEmail?.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const filteredProducts = allProducts.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredInvestments = allInvestments.filter(i =>
    i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.owner?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredCategories = allCategories.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── AI Agent: Run Gemini-powered web search ──
  const runAgentSearch = async () => {
    setAgentLoading(true);
    setAgentError("");
    setAgentResults([]);
    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const apiKey = process.env.REACT_APP_GEMINI_KEY;
    if (!apiKey) {
      setAgentError("Gemini API key not configured. Add REACT_APP_GEMINI_KEY=your_key to a .env file in the project root, then restart the dev server.");
      setAgentLoading(false);
      return;
    }
    const prompt = `Today is ${today}.

Search the web RIGHT NOW for people in NIGERIA who have recently posted on social media platforms (Twitter/X, Facebook, LinkedIn, WhatsApp groups, Jiji Nigeria, Nairaland, agricultural forums, Nigerian buy-and-sell groups, Facebook Groups Nigeria) looking to BUY or SOURCE farm produce / agricultural commodities.

PRIORITY: Nigeria is the FIRST and PRIMARY focus. Search specifically for Nigerian cities like Lagos, Abuja, Kano, Port Harcourt, Ibadan, Enugu, Onitsha, Kaduna, etc. If results are insufficient, expand to Ghana, Kenya, and other African countries as secondary.

For each post/lead you find, extract exactly these fields:
- produce: the specific farm product they want to buy (e.g. "Maize", "Cocoa", "Cassava", "Tomatoes", "Rice", "Yam", "Palm Oil", "Groundnut", "Soybean", "Pepper", "Garlic", "Onions", "Plantain", etc.)
- quantity: the quantity they mentioned (e.g. "50 tons", "10 bags", "500kg") — use "Not specified" if not mentioned
- contact: their phone number, WhatsApp number, email address, or social media handle/username — use the most specific one available; use "Not found" if none available
- contactType: exactly one of "Phone", "Email", "Username", or "Unknown"
- platform: the social media platform where they posted — exactly one of "Twitter", "Facebook", "LinkedIn", "WhatsApp", "Forum", "Jiji", "Nairaland", "Tonaton", or "Other"
- location: their Nigerian city/state if mentioned (e.g. "Lagos, Nigeria", "Kano, Nigeria") — use "Nigeria" as fallback if country is Nigeria but city unknown
- postSnippet: a direct quote or close paraphrase of their post (max 140 characters)
- postUrl: the direct URL link to the post if available, otherwise empty string ""

Return ONLY a valid JSON array. No markdown, no code fences, no explanation — raw JSON array starting with [ and ending with ]. Aim for 8 results maximum. If no real results found, return [].`;

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            tools: [{ google_search: {} }],
            generationConfig: { temperature: 0.1, topP: 0.9, maxOutputTokens: 8192 }
          })
        }
      );
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData?.error?.message || `API error ${response.status}`);
      }
      const data = await response.json();
      const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text || "[]";
      const cleaned = rawText.replace(/```json/gi, "").replace(/```/g, "").trim();

      // ── Robust multi-strategy parser ──
      const salvageObjects = (text) => {
        // Strategy 1: full parse
        try { const r = JSON.parse(text); if (Array.isArray(r)) return r; } catch { }
        // Strategy 2: close a truncated array (...,{partial)] → try closing last incomplete object
        const tryClosed = (t) => {
          // Remove the last incomplete item (everything after the last complete "},")
          const lastComplete = t.lastIndexOf("},");
          if (lastComplete > 0) {
            const trimmed = t.substring(0, lastComplete + 1) + "]";
            try { const r = JSON.parse(trimmed); if (Array.isArray(r) && r.length > 0) return r; } catch { }
          }
          // Also try just closing with ]
          try { const r = JSON.parse(t + "]"); if (Array.isArray(r) && r.length > 0) return r; } catch { }
          return null;
        };
        const closedResult = tryClosed(cleaned);
        if (closedResult) return closedResult;
        // Strategy 3: brace-counting — extract every complete top-level {...} object
        const objects = [];
        let depth = 0, start = -1;
        for (let i = 0; i < text.length; i++) {
          if (text[i] === "{") { if (depth === 0) start = i; depth++; }
          else if (text[i] === "}") {
            depth--;
            if (depth === 0 && start !== -1) {
              try { objects.push(JSON.parse(text.substring(start, i + 1))); } catch { }
              start = -1;
            }
          }
        }
        return objects;
      };

      const parsed = salvageObjects(cleaned);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error("No usable leads could be extracted from the AI response.");
      }
      const withIds = parsed.map((r, i) => ({ ...r, _id: `agent_${Date.now()}_${i}` }));
      setAgentResults(withIds);
      setAgentSearchCount(c => c + 1);
      if (withIds.length === 0) setAgentError("No buyer leads found for today. Try again in a few minutes — social media activity changes constantly.");
    } catch (err) {
      console.error("Agent search failed:", err);
      setAgentError(`Search failed: ${err.message}`);
    } finally {
      setAgentLoading(false);
    }
  };

  // ── AI Agent: Push a result to the marketplace ──
  const pushToMarketplace = async (result) => {
    const adminId = cookies.username;
    const uniqueId = `${adminId}_ai_${Date.now()}`;
    const contactType = ["Phone", "Email", "Username"].includes(result.contactType) ? result.contactType : "Email";
    const qty = result.quantity && result.quantity !== "Not specified" ? result.quantity : "As discussed";
    const edits = cardEdits[result._id] || {};
    const customPrice = edits.price?.trim() || "";
    const autoDesc = `[AI Agent Lead | ${result.platform || "Web"} | ${result.location && result.location !== "Not specified" ? result.location : "Nigeria"}] ${result.postSnippet || ""}${result.postUrl ? ` | Source: ${result.postUrl}` : ""}`;
    const newRequest = {
      id: uniqueId,
      product: result.produce || "Farm Produce",
      quantity: qty,
      quantityValue: qty,
      quantityUnit: "Units",
      unitPrice: customPrice || "0",
      price: customPrice ? `₦${customPrice} / unit` : `Qty: ${qty}`,
      buyer: `Agrivestia AI Lead`,
      buyer_contact: result.contact && result.contact !== "Not found" ? result.contact : "",
      buyer_contact_type: contactType,
      description: edits.description?.trim() || autoDesc,
      time: new Date().toLocaleDateString(),
      paymentMode: "Agrivestia Escrow",
      paymentTerm: "",
      deliveryType: "",
      deliveryDays: "",
      escrowAmount: "0",
      status: "0",
      source: "ai_agent_search",
      sourcePlatform: result.platform || "",
      sourceUrl: result.postUrl || ""
    };
    try {
      const { setDoc, doc: fsDoc } = await import("firebase/firestore");
      await setDoc(fsDoc(db, "buyers_request", uniqueId), newRequest);
      setPushedIds(prev => new Set([...prev, result._id]));
      showToast("success", "Pushed to Marketplace", `"${result.produce}" request is now live on the marketplace.`);
    } catch (err) {
      console.error(err);
      showToast("error", "Push Failed", "Could not post to marketplace. Try again.");
    }
  };

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet>
        <title>Admin Dashboard — Agrivestia</title>
      </Helmet>

      {/* Mobile overlay */}
      {show_mobile_menu && <div className="overlay" onClick={() => setMobileMenu(false)} />}

      {/* Sidebar */}
      <aside style={{
        width: "260px", height: "100vh", background: "var(--color-white)",
        borderRight: "1px solid var(--color-gray-100)", display: "flex", flexDirection: "column",
        position: "fixed", top: 0, left: 0, zIndex: 999,
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
      }} className={`sidebar ${show_mobile_menu ? "open" : ""}`}>
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={17} color="white" />
            </div>
            <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>ADMIN</span>
          </div>
        </div>
        <nav style={{ padding: "16px 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "4px 8px 10px" }}>Menu</div>
          {[
            { icon: <Users size={18} />, label: "Users", key: "users" },
            { icon: <MessageSquare size={18} />, label: "Inquiries", key: "inquiries" },
            { icon: <AlertCircle size={18} />, label: "Support Tickets", key: "tickets" },
            { icon: <Layers size={18} />, label: "Categories", key: "categories" },
            { icon: <Package size={18} />, label: "Products", key: "products" },
            { icon: <TrendingUp size={18} />, label: "Investments", key: "investments" },
            { icon: <Globe size={18} />, label: "Central Market", key: "central_market" },
            { icon: <UserCheck size={18} />, label: "Agent Requests", key: "agent_requests" },
            { icon: <Award size={18} />, label: "Agents", key: "agents" },
            { icon: <TrendingUp size={18} />, label: "Finances", key: "finances" },
            { icon: <Sparkles size={18} />, label: "Blog Posts", key: "blogs" },
            { icon: <Bot size={18} />, label: "AI Agent Search", key: "ai_agent" }
          ].map((item) => (
            <button key={item.key} onClick={() => { setMobileMenu(false); setActiveTab(item.key); }}
              className={`sidebar-link ${activeTab === item.key ? "active" : ""}`}
              style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
              <span style={{ flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: "14px" }}>{item.label}</span>
              {activeTab === item.key && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 12px 20px", borderTop: "1px solid var(--color-gray-100)" }}>
          <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }}
            style={{ width: "100%", border: "none", cursor: "pointer" }}>
            <LogOut size={17} />
            <span style={{ fontSize: "14px" }}>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ minHeight: "100vh", marginLeft: "260px" }} className="main-content">
        {/* Top bar */}
        <div style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 16px", minHeight: "64px", display: "flex", alignItems: "center", gap: "16px", position: "sticky", top: 0, zIndex: 100 }}>
          <button onClick={() => setMobileMenu(!show_mobile_menu)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", display: "none" }}
            className="show-mobile">
            {show_mobile_menu ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)" }}>
              {activeTab === "users" ? "User Management" : activeTab === "inquiries" ? "Customer Inquiries" : activeTab === "tickets" ? "Support Tickets" : activeTab === "categories" ? "Category Management" : activeTab === "products" ? "Product Management" : activeTab === "ai_agent" ? "AI Agent Search" : activeTab === "central_market" ? "Central Market Requests" : activeTab === "agent_requests" ? "Agent Applications" : activeTab === "finances" ? "Financial Management" : activeTab === "agents" ? "Agent Management" : "Investment Management"}
            </div>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{
            position: "fixed", top: "20px", right: "20px", zIndex: 1000,
            padding: "16px 20px", borderRadius: "var(--radius-lg)",
            background: toast.type === "error" ? "var(--color-danger-light)" : "var(--color-success-light)",
            border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
            color: toast.type === "error" ? "#7f1d1d" : "#15803d",
            display: "flex", alignItems: "center", gap: "12px"
          }}>
            {toast.type === "error" ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px" }}>{toast.title}</div>
              <div style={{ fontSize: "13px", opacity: 0.8 }}>{toast.msg}</div>
            </div>
          </div>
        )}

        <div className="section-pad" style={{ padding: "var(--space-6) var(--space-4)" }}>
          {/* Platform Stats Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px", marginBottom: "32px" }}>
            <div className="card" style={{ padding: "20px", display: "flex", alignItems: "center", gap: "16px", background: "linear-gradient(135deg, #064e3b, #065f46)", border: "none" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendingUp size={24} color="white" />
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", fontWeight: 600, textTransform: "uppercase" }}>Agrivestia Wallet</div>
                <div style={{ fontSize: "20px", fontWeight: 800, color: "white" }}>₦{adminWallet.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</div>
              </div>
            </div>
            {/* You can add more summary cards here if needed */}
          </div>

          {/* Inquiries Tab */}
          {activeTab === "inquiries" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "14px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allInquiries.length}</span>
                  {" "}&mdash;{" "}
                  Open: <span style={{ fontWeight: 700, color: "var(--color-warning)" }}>{allInquiries.filter(i => i.status === "open").length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedInquiry ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* List */}
                <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                  {allInquiries.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--color-gray-400)" }}>
                      <MessageSquare size={36} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
                      <div style={{ fontWeight: 600 }}>No inquiries yet</div>
                    </div>
                  ) : allInquiries.map(inq => (
                    <div key={inq.id} onClick={() => setSelectedInquiry(inq)}
                      style={{
                        padding: "16px 20px", marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedInquiry?.id === inq.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedInquiry?.id === inq.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer", transition: "all 0.2s"
                      }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "6px" }}>
                        <div style={{ fontWeight: 700, fontSize: "14px", color: "var(--color-gray-900)" }}>{inq.createdByName || "Anonymous"}</div>
                        <span style={{
                          fontSize: "10px", fontWeight: 700, padding: "2px 10px", borderRadius: "20px",
                          background: inq.status === "resolved" ? "var(--color-success-light)" : "var(--color-warning-light, #fef3c7)",
                          color: inq.status === "resolved" ? "var(--color-success)" : "#b45309"
                        }}>{inq.status === "resolved" ? "Resolved" : "Open"}</span>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--color-gray-500)", marginBottom: "4px" }}>{inq.createdByEmail}</div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{inq.description}</div>
                      <div style={{ fontSize: "11px", color: "var(--color-gray-400)", marginTop: "6px" }}>
                        {inq.timestamp?.toDate ? inq.timestamp.toDate().toLocaleString() : ""}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detail panel */}
                {selectedInquiry && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "17px", fontWeight: 700, color: "var(--color-gray-900)" }}>Inquiry Details</h3>
                      <button onClick={() => setSelectedInquiry(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>From</div>
                        <div style={{ fontWeight: 600 }}>{selectedInquiry.createdByName}</div>
                        <div style={{ fontSize: "13px", color: "var(--color-primary)" }}>{selectedInquiry.createdByEmail}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>User ID</div>
                        <div style={{ fontSize: "13px" }}>{selectedInquiry.createdBy || "—"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Status</div>
                        <span style={{
                          fontSize: "12px", fontWeight: 700, padding: "3px 12px", borderRadius: "20px",
                          background: selectedInquiry.status === "resolved" ? "var(--color-success-light)" : "var(--color-warning-light, #fef3c7)",
                          color: selectedInquiry.status === "resolved" ? "var(--color-success)" : "#b45309"
                        }}>{selectedInquiry.status === "resolved" ? "Resolved" : "Open"}</span>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Message</div>
                        <div style={{ fontSize: "14px", color: "var(--color-gray-700)", lineHeight: 1.7, padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", whiteSpace: "pre-wrap" }}>
                          {selectedInquiry.description}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px" }}>
                        {selectedInquiry.status !== "resolved" && (
                          <button
                            onClick={async () => {
                              try {
                                await updateDoc(doc(db, "admin_tickets", selectedInquiry.id), { status: "resolved" });
                                const updated = { ...selectedInquiry, status: "resolved" };
                                setAllInquiries(allInquiries.map(i => i.id === selectedInquiry.id ? updated : i));
                                setSelectedInquiry(updated);
                                showToast("success", "Resolved", "Inquiry marked as resolved.");
                              } catch { showToast("error", "Error", "Could not update inquiry."); }
                            }}
                            className="btn-primary" style={{ padding: "10px 16px", fontSize: "13px", background: "var(--color-success)", borderColor: "var(--color-success)", justifyContent: "center" }}>
                            <CheckCircle2 size={14} /> Mark Resolved
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            if (!window.confirm("Delete this inquiry?")) return;
                            try {
                              await deleteDoc(doc(db, "admin_tickets", selectedInquiry.id));
                              setAllInquiries(allInquiries.filter(i => i.id !== selectedInquiry.id));
                              setSelectedInquiry(null);
                              showToast("success", "Deleted", "Inquiry removed.");
                            } catch { showToast("error", "Error", "Could not delete inquiry."); }
                          }}
                          className="btn-ghost" style={{ padding: "10px 16px", fontSize: "13px", color: "var(--color-danger)", borderColor: "var(--color-danger)" }}>
                          <Trash2 size={14} /> Delete
                        </button>
                        {selectedInquiry.createdByEmail && (
                          <a href={`mailto:${selectedInquiry.createdByEmail}`} className="btn-ghost" style={{ padding: "10px 16px", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px", textDecoration: "none", color: "var(--color-primary)", borderColor: "var(--color-primary)", border: "1px solid", borderRadius: "var(--radius-md)" }}>
                            <Mail size={14} /> Reply via Email
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search by name, email or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allUsers.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedUser ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Users List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedUser?.id === user.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedUser?.id === user.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedUser?.id === user.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedUser?.id === user.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{user.name || "N/A"}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)" }}>{user.id}</div>
                        </div>
                        {user.accountStatus === "frozen" && (
                          <Lock size={14} color="#ef4444" />
                        )}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-600)" }}>
                        Balance: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>₦{(user.wallet || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</span>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No users found
                    </div>
                  )}
                </div>

                {/* User Details */}
                {selectedUser && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                      User Details
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Name</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedUser.name || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>User ID</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedUser.id}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Email</div>
                        <div style={{ fontSize: "14px", color: "var(--color-gray-800)" }}>{selectedUser.email || selectedUser.email_address || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Registration Date</div>
                        <div style={{ fontSize: "14px", color: "var(--color-gray-800)" }}>{selectedUser.registrationDate ? new Date(selectedUser.registrationDate).toLocaleString() : "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Last Login</div>
                        <div style={{ fontSize: "14px", color: "var(--color-gray-800)" }}>{selectedUser.lastLoginDate ? new Date(selectedUser.lastLoginDate).toLocaleString() : "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Wallet Balance</div>
                        <div style={{ fontSize: "16px", fontWeight: 800, color: "var(--color-primary)" }}>₦{(selectedUser.wallet || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Account Status</div>
                        <div style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background: selectedUser.accountStatus === "frozen" ? "var(--color-danger-light)" : "var(--color-success-light)",
                          color: selectedUser.accountStatus === "frozen" ? "#7f1d1d" : "#15803d"
                        }}>
                          {selectedUser.accountStatus === "frozen" ? "FROZEN" : selectedUser.accountStatus === "deleted" ? "DELETED" : "ACTIVE"}
                        </div>
                      </div>

                      <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        {selectedUser.accountStatus !== "deleted" && (
                          <>
                            <button
                              onClick={() => handleFreezeAccount(selectedUser.id, selectedUser.accountStatus !== "frozen")}
                              style={{
                                padding: "10px", borderRadius: "var(--radius-md)", border: "1px solid",
                                fontSize: "13px", fontWeight: 600, cursor: "pointer",
                                borderColor: selectedUser.accountStatus === "frozen" ? "var(--color-success)" : "var(--color-danger)",
                                background: "white",
                                color: selectedUser.accountStatus === "frozen" ? "var(--color-success)" : "var(--color-danger)"
                              }}>
                              {selectedUser.accountStatus === "frozen" ? "✓ Unfreeze Account" : "🔒 Freeze Account"}
                            </button>

                            <button
                              onClick={() => handleToggleAccountStatus(selectedUser, selectedUser.accountStatus === 'disabled' ? 'active' : 'disabled')}
                              style={{
                                padding: "10px", borderRadius: "var(--radius-md)", border: "1px solid",
                                fontSize: "13px", fontWeight: 600, cursor: "pointer",
                                borderColor: selectedUser.accountStatus === 'disabled' ? "var(--color-success)" : "var(--color-danger)",
                                background: "white",
                                color: selectedUser.accountStatus === 'disabled' ? "var(--color-success)" : "var(--color-danger)"
                              }}>
                              {selectedUser.accountStatus === 'disabled' ? "✓ Enable Account" : "⊘ Disable Account"}
                            </button>
                            <button
                              onClick={() => handleDeleteAccount(selectedUser.id)}
                              style={{
                                padding: "10px", borderRadius: "var(--radius-md)",
                                fontSize: "13px", fontWeight: 600, cursor: "pointer",
                                background: "var(--color-danger)", color: "white", border: "none"
                              }}>
                              <Trash2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                              Delete Account
                            </button>

                            {selectedUser.isAgent && (
                              <div style={{ marginTop: "16px", borderTop: "1px dashed var(--color-gray-200)", paddingTop: "16px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-danger)", textTransform: "uppercase", marginBottom: "8px" }}>Revoke Agent License</div>
                                <textarea
                                  placeholder="Reason for revocation..."
                                  value={revokeReason}
                                  onChange={(e) => setRevokeReason(e.target.value)}
                                  className="input-field"
                                  rows={2}
                                  style={{ fontSize: "12px" }}
                                />
                                <button
                                  onClick={() => handleRevokeAgent(selectedUser)}
                                  style={{
                                    width: "100%", padding: "10px", borderRadius: "var(--radius-md)",
                                    fontSize: "13px", fontWeight: 600, cursor: "pointer",
                                    background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca"
                                  }}>
                                  Revoke Agent Status
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === "tickets" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allTickets.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedTicket ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Tickets List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredTickets.map(ticket => (
                    <div
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedTicket?.id === ticket.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedTicket?.id === ticket.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedTicket?.id === ticket.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedTicket?.id === ticket.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{ticket.title}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)", marginTop: "2px" }}>{ticket.type}</div>
                        </div>
                        <div style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                          fontWeight: 700,
                          background: ticket.status === "resolved" ? "var(--color-success-light)" : ticket.status === "in_progress" ? "var(--color-primary-light)" : "var(--color-danger-light)",
                          color: ticket.status === "resolved" ? "#15803d" : ticket.status === "in_progress" ? "var(--color-primary)" : "#7f1d1d"
                        }}>
                          {ticket.status?.toUpperCase()}
                        </div>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--color-gray-600)" }}>
                        From: {ticket.createdByName || ticket.createdBy}
                      </div>
                    </div>
                  ))}
                  {filteredTickets.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No tickets found
                    </div>
                  )}
                </div>

                {/* Ticket Details */}
                {selectedTicket && (
                  <div className="card" style={{ padding: "24px", height: "fit-content", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "16px", color: "var(--color-gray-900)" }}>
                      {selectedTicket.title}
                    </h3>

                    <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                      <div style={{
                        padding: "4px 10px", borderRadius: "4px",
                        fontSize: "11px", fontWeight: 700,
                        background: "var(--color-gray-100)", color: "var(--color-gray-700)"
                      }}>
                        TYPE: {selectedTicket.type}
                      </div>
                      <div style={{
                        padding: "4px 10px", borderRadius: "4px",
                        fontSize: "11px", fontWeight: 700,
                        background: selectedTicket.status === "resolved" ? "var(--color-success-light)" : "var(--color-danger-light)",
                        color: selectedTicket.status === "resolved" ? "#15803d" : "#7f1d1d"
                      }}>
                        STATUS: {selectedTicket.status?.toUpperCase()}
                      </div>
                    </div>

                    <div style={{ fontSize: "13px", color: "var(--color-gray-600)", marginBottom: "16px", lineHeight: 1.5 }}>
                      <strong>Description:</strong>
                      <div style={{ marginTop: "8px", padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)" }}>
                        {selectedTicket.description}
                      </div>
                    </div>

                    {/* Messages */}
                    <div style={{ flex: 1, overflow: "auto", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid var(--color-gray-100)" }}>
                      {selectedTicket.messages?.map((msg, idx) => (
                        <div key={idx} style={{ marginBottom: "12px" }}>
                          <div style={{ fontSize: "11px", fontWeight: 700, color: msg.sender === "admin" ? "var(--color-primary)" : "var(--color-gray-500)", textTransform: "uppercase" }}>
                            {msg.senderName || msg.sender}
                          </div>
                          <div style={{
                            padding: "10px 12px", borderRadius: "var(--radius-md)",
                            background: msg.sender === "admin" ? "var(--color-primary-light)" : "var(--color-gray-100)",
                            fontSize: "13px", color: "var(--color-gray-800)", lineHeight: 1.4, marginTop: "4px"
                          }}>
                            {msg.message}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    {selectedTicket.status !== "resolved" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <select
                          value={ticketStatus || selectedTicket.status}
                          onChange={(e) => setTicketStatus(e.target.value)}
                          className="input-field"
                          style={{ marginBottom: 0 }}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        <textarea
                          value={ticketReply}
                          onChange={(e) => setTicketReply(e.target.value)}
                          placeholder="Type your reply..."
                          rows={2}
                          className="input-field"
                          style={{ marginBottom: 0 }}
                        />
                        <button
                          onClick={handleReplyTicket}
                          className="btn-primary"
                          style={{ justifyContent: "center", padding: "10px" }}
                        >
                          <Send size={16} /> Send Reply
                        </button>
                      </div>
                    )}

                    {selectedTicket.status !== "resolved" && (
                      <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--color-gray-100)" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--color-gray-700)", marginBottom: "8px" }}>
                          Mark as Resolved
                        </label>
                        <textarea
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                          placeholder="Explain the resolution..."
                          rows={2}
                          className="input-field"
                          style={{ marginBottom: "8px" }}
                        />
                        <button
                          onClick={handleResolveTicket}
                          style={{
                            width: "100%", padding: "10px", background: "var(--color-success)", color: "white",
                            border: "none", borderRadius: "var(--radius-md)", fontWeight: 600, cursor: "pointer"
                          }}
                        >
                          <CheckCircle2 size={16} style={{ marginRight: "6px", display: "inline" }} />
                          Resolve Ticket
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Inquiries Tab */}
          {activeTab === "inquiries" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search inquiries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{filteredInquiries.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedTicket ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Inquiries List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredInquiries.map(inquiry => (
                    <div
                      key={inquiry.id}
                      onClick={() => setSelectedTicket(inquiry)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedTicket?.id === inquiry.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedTicket?.id === inquiry.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedTicket?.id === inquiry.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedTicket?.id === inquiry.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{inquiry.createdByName}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)", marginTop: "2px" }}>{inquiry.createdByEmail}</div>
                        </div>
                        <div style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                          fontWeight: 700,
                          background: inquiry.status === "resolved" ? "var(--color-success-light)" : inquiry.status === "in_progress" ? "var(--color-primary-light)" : "var(--color-danger-light)",
                          color: inquiry.status === "resolved" ? "#15803d" : inquiry.status === "in_progress" ? "var(--color-primary)" : "#7f1d1d"
                        }}>
                          {inquiry.status?.toUpperCase()}
                        </div>
                      </div>
                      <div style={{ fontSize: "12px", color: "var(--color-gray-600)" }}>
                        {inquiry.description?.substring(0, 80)}...
                      </div>
                    </div>
                  ))}
                  {filteredInquiries.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No inquiries found
                    </div>
                  )}
                </div>

                {/* Inquiry Details */}
                {selectedTicket && selectedTicket.type === "contact_us" && (
                  <div className="card" style={{ padding: "24px", height: "fit-content", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "16px", color: "var(--color-gray-900)" }}>
                      {selectedTicket.createdByName}
                    </h3>

                    <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                      <div style={{
                        padding: "4px 10px", borderRadius: "4px",
                        fontSize: "11px", fontWeight: 700,
                        background: "var(--color-gray-100)", color: "var(--color-gray-700)"
                      }}>
                        EMAIL: {selectedTicket.createdByEmail}
                      </div>
                      <div style={{
                        padding: "4px 10px", borderRadius: "4px",
                        fontSize: "11px", fontWeight: 700,
                        background: selectedTicket.status === "resolved" ? "var(--color-success-light)" : "var(--color-danger-light)",
                        color: selectedTicket.status === "resolved" ? "#15803d" : "#7f1d1d"
                      }}>
                        STATUS: {selectedTicket.status?.toUpperCase()}
                      </div>
                    </div>

                    <div style={{ fontSize: "13px", color: "var(--color-gray-600)", marginBottom: "16px", lineHeight: 1.5 }}>
                      <strong>Message:</strong>
                      <div style={{ marginTop: "8px", padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)" }}>
                        {selectedTicket.description}
                      </div>
                    </div>

                    {/* Messages */}
                    <div style={{ flex: 1, overflow: "auto", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid var(--color-gray-100)" }}>
                      {selectedTicket.messages?.map((msg, idx) => (
                        <div key={idx} style={{ marginBottom: "12px" }}>
                          <div style={{ fontSize: "11px", fontWeight: 700, color: msg.sender === "admin" ? "var(--color-primary)" : "var(--color-gray-500)", textTransform: "uppercase" }}>
                            {msg.senderName || msg.sender}
                          </div>
                          <div style={{
                            padding: "10px 12px", borderRadius: "var(--radius-md)",
                            background: msg.sender === "admin" ? "var(--color-primary-light)" : "var(--color-gray-100)",
                            fontSize: "13px", color: "var(--color-gray-800)", lineHeight: 1.4, marginTop: "4px"
                          }}>
                            {msg.message}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    {selectedTicket.status !== "resolved" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <select
                          value={ticketStatus || selectedTicket.status}
                          onChange={(e) => setTicketStatus(e.target.value)}
                          className="input-field"
                          style={{ marginBottom: 0 }}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        <textarea
                          value={ticketReply}
                          onChange={(e) => setTicketReply(e.target.value)}
                          placeholder="Type your reply..."
                          rows={2}
                          className="input-field"
                          style={{ marginBottom: 0 }}
                        />
                        <button
                          onClick={handleReplyTicket}
                          className="btn-primary"
                          style={{ justifyContent: "center", padding: "10px" }}
                        >
                          <Send size={16} /> Send Reply
                        </button>
                      </div>
                    )}

                    {selectedTicket.status !== "resolved" && (
                      <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--color-gray-100)" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "var(--color-gray-700)", marginBottom: "8px" }}>
                          Mark as Resolved
                        </label>
                        <textarea
                          value={resolution}
                          onChange={(e) => setResolution(e.target.value)}
                          placeholder="Explain the resolution..."
                          rows={2}
                          className="input-field"
                          style={{ marginBottom: "8px" }}
                        />
                        <button
                          onClick={handleResolveTicket}
                          style={{
                            width: "100%", padding: "10px", background: "var(--color-success)", color: "white",
                            border: "none", borderRadius: "var(--radius-md)", fontWeight: 600, cursor: "pointer"
                          }}
                        >
                          <CheckCircle2 size={16} style={{ marginRight: "6px", display: "inline" }} />
                          Resolve Inquiry
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === "products" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <button
                  onClick={() => {
                    setIsEditingProduct(false);
                    setProductForm({ name: "", amount: "", measurment: "" });
                    setShowProductModal(true);
                  }}
                  style={{
                    padding: "10px 16px", background: "var(--color-primary)", color: "white",
                    border: "none", borderRadius: "var(--radius-md)", fontWeight: 600,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "6px"
                  }}
                >
                  <Plus size={18} /> Add Product
                </button>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allProducts.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedProduct ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Products List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredProducts.map(product => (
                    <div
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedProduct?.id === product.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedProduct?.id === product.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedProduct?.id === product.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedProduct?.id === product.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{product.name || "N/A"}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)" }}>{product.id}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-600)", display: "flex", justifyContent: "space-between" }}>
                        <span>Price: <span style={{ fontWeight: 700 }}>₦{(product.amount || "0").toLocaleString()}</span></span>
                        <span>Unit: <span style={{ fontWeight: 700 }}>{product.measurment || "N/A"}</span></span>
                      </div>
                    </div>
                  ))}
                  {filteredProducts.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No products found
                    </div>
                  )}
                </div>

                {/* Product Details */}
                {selectedProduct && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                      Product Details
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Product Name</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedProduct.name || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Price (Amount)</div>
                        <div style={{ fontSize: "16px", fontWeight: 800, color: "var(--color-primary)" }}>₦{(selectedProduct.amount || "0").toLocaleString()}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Measurement Unit</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedProduct.measurment || "N/A"}</div>
                      </div>

                      <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <button
                          onClick={() => {
                            setIsEditingProduct(true);
                            setProductForm({
                              name: selectedProduct.name,
                              amount: selectedProduct.amount,
                              measurment: selectedProduct.measurment
                            });
                            setShowProductModal(true);
                          }}
                          style={{
                            padding: "10px", borderRadius: "var(--radius-md)", border: "1px solid var(--color-primary)",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "white", color: "var(--color-primary)"
                          }}>
                          <Edit2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                          Edit Product
                        </button>
                        <button
                          onClick={handleDeleteProduct}
                          style={{
                            padding: "10px", borderRadius: "var(--radius-md)",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "var(--color-danger)", color: "white", border: "none"
                          }}>
                          <Trash2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                          Delete Product
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Product Modal */}
          {showProductModal && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center",
              justifyContent: "center", zIndex: 2000
            }} onClick={() => setShowProductModal(false)}>
              <div className="card" style={{
                padding: "28px", maxWidth: "400px", width: "90%"
              }} onClick={e => e.stopPropagation()}>
                <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                  {isEditingProduct ? "Edit Product" : "Create New Product"}
                </h3>

                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  <input
                    type="text"
                    placeholder="Product Name"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                  />

                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Category (Select or type new)"
                      value={productForm.category}
                      onChange={(e) => {
                        setProductForm({ ...productForm, category: e.target.value });
                        setProductCategorySuggestions(true);
                      }}
                      onFocus={() => setProductCategorySuggestions(true)}
                      onBlur={() => setTimeout(() => setProductCategorySuggestions(false), 200)}
                      className="input-field"
                      style={{ marginBottom: 0 }}
                    />
                    {productCategorySuggestions && (
                      <div style={{
                        position: "absolute", top: "100%", left: 0, right: 0,
                        backgroundColor: "white", border: "1px solid var(--color-gray-200)",
                        borderTop: "none", borderRadius: "0 0 6px 6px",
                        maxHeight: "160px", overflow: "auto", zIndex: 10
                      }}>
                        {availableCategories
                          .filter(catName => catName.toLowerCase().includes((productForm.category || "").toLowerCase()))
                          .map((catName, idx) => (
                            <div
                              key={idx}
                              onClick={() => {
                                setProductForm({ ...productForm, category: catName });
                                setProductCategorySuggestions(false);
                              }}
                              style={{
                                padding: "10px 12px", cursor: "pointer",
                                borderBottom: "1px solid var(--color-gray-100)",
                                fontSize: "13px", color: "var(--color-gray-800)",
                                transition: "background 0.15s"
                              }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = "var(--color-gray-50)"}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                            >
                              {catName}
                            </div>
                          ))}
                        {availableCategories.filter(catName => catName.toLowerCase().includes((productForm.category || "").toLowerCase())).length === 0 && productForm.category && (
                          <div style={{ padding: "10px 12px", color: "var(--color-gray-400)", fontSize: "13px" }}>
                            Create new category: <strong>"{productForm.category}"</strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <input
                    type="text"
                    placeholder="Price (Amount)"
                    value={productForm.amount}
                    onChange={(e) => setProductForm({ ...productForm, amount: e.target.value })}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                  />

                  <input
                    type="text"
                    placeholder="Measurement Unit (e.g., Tons, Kg, Bags)"
                    value={productForm.measurment}
                    onChange={(e) => setProductForm({ ...productForm, measurment: e.target.value })}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                  />



                  <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                    <button
                      onClick={() => setShowProductModal(false)}
                      style={{
                        flex: 1, padding: "10px", border: "1px solid var(--color-gray-300)",
                        background: "white", borderRadius: "var(--radius-md)", fontWeight: 600,
                        cursor: "pointer", color: "var(--color-gray-700)"
                      }}>
                      Cancel
                    </button>
                    <button
                      onClick={isEditingProduct ? handleUpdateProduct : handleCreateProduct}
                      className="btn-primary"
                      style={{ flex: 1, padding: "10px", justifyContent: "center" }}>
                      {isEditingProduct ? "Update Product" : "Create Product"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category Modal */}
          {showCategoryModal && (
            <div style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center",
              justifyContent: "center", zIndex: 2000
            }} onClick={() => setShowCategoryModal(false)}>
              <div className="card" style={{
                padding: "28px", maxWidth: "400px", width: "90%"
              }} onClick={e => e.stopPropagation()}>
                <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                  {isEditingCategory ? "Edit Category" : "Create New Category"}
                </h3>

                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  <input
                    type="text"
                    placeholder="Category Name (e.g., Cash crops, Vegetables)"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ name: e.target.value })}
                    className="input-field"
                    style={{ marginBottom: 0 }}
                  />

                  <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                    <button
                      onClick={() => setShowCategoryModal(false)}
                      style={{
                        flex: 1, padding: "10px", border: "1px solid var(--color-gray-300)",
                        background: "white", borderRadius: "var(--radius-md)", fontWeight: 600,
                        cursor: "pointer", color: "var(--color-gray-700)"
                      }}>
                      Cancel
                    </button>
                    <button
                      onClick={isEditingCategory ? handleUpdateCategory : handleCreateCategory}
                      className="btn-primary"
                      style={{ flex: 1, padding: "10px", justifyContent: "center" }}>
                      {isEditingCategory ? "Update Category" : "Create Category"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Categories Tab */}
          {activeTab === "categories" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search categories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <button
                  onClick={() => {
                    setIsEditingCategory(false);
                    setCategoryForm({ name: "" });
                    setShowCategoryModal(true);
                  }}
                  style={{
                    padding: "10px 16px", background: "var(--color-primary)", color: "white",
                    border: "none", borderRadius: "var(--radius-md)", fontWeight: 600,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "6px"
                  }}
                >
                  <Plus size={18} /> Add Category
                </button>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allCategories.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedCategory ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Categories List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredCategories.map(category => (
                    <div
                      key={category.id}
                      onClick={() => setSelectedCategory(category)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedCategory?.id === category.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedCategory?.id === category.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedCategory?.id === category.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedCategory?.id === category.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{category.name || "N/A"}</div>
                      <div style={{ fontSize: "12px", color: "var(--color-gray-500)", marginTop: "4px" }}>
                        {allProducts.filter(p => p.category === category.id).length} products
                      </div>
                    </div>
                  ))}
                  {filteredCategories.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No categories found
                    </div>
                  )}
                </div>

                {/* Category Details */}
                {selectedCategory && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                      Category Details
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Name</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedCategory.name || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Products in Category</div>
                        <div style={{ fontSize: "16px", fontWeight: 800, color: "var(--color-primary)" }}>
                          {allProducts.filter(p => p.category === selectedCategory.id).length}
                        </div>
                      </div>

                      <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
                        <button
                          onClick={() => {
                            setIsEditingCategory(true);
                            setCategoryForm({ name: selectedCategory.name });
                            setShowCategoryModal(true);
                          }}
                          style={{
                            padding: "10px", borderRadius: "var(--radius-md)", border: "1px solid var(--color-primary)",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "white", color: "var(--color-primary)"
                          }}>
                          <Edit2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                          Edit Category
                        </button>
                        <button
                          onClick={handleDeleteCategory}
                          style={{
                            padding: "10px", borderRadius: "var(--radius-md)",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "var(--color-danger)", color: "white", border: "none"
                          }}>
                          <Trash2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                          Delete Category
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Agent Requests Tab */}
          {activeTab === "agent_requests" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-600)" }}>
                  Pending Applications: <span style={{ color: "var(--color-primary)" }}>{allAgentRequests.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedAgentRequest ? "1fr 1fr" : "1fr", gap: "24px" }}>
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {allAgentRequests.map(req => (
                    <div
                      key={req.id}
                      onClick={() => setSelectedAgentRequest(req)}
                      style={{
                        padding: "16px 20px", marginBottom: "8px", borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedAgentRequest?.id === req.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedAgentRequest?.id === req.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer", transition: "all 0.2s"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{req.userName}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)" }}>{req.userId}</div>
                        </div>
                        <ChevronRight size={16} color="var(--color-gray-400)" />
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--color-gray-400)", marginTop: "8px" }}>
                        Requested: {new Date(req.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {allAgentRequests.length === 0 && (
                    <div className="card" style={{ padding: "40px", textAlign: "center", color: "var(--color-gray-400)" }}>
                      <UserCheck size={40} style={{ opacity: 0.2, marginBottom: "12px" }} />
                      <div>No pending agent applications.</div>
                    </div>
                  )}
                </div>

                {selectedAgentRequest && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                      <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "var(--color-primary)" }}>
                        {selectedAgentRequest.userName[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "16px" }}>{selectedAgentRequest.userName}</div>
                        <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>Application for Agent Status</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
                      <div style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-100)" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "4px" }}>Submission Date</div>
                        <div style={{ fontSize: "14px" }}>{new Date(selectedAgentRequest.timestamp).toLocaleString()}</div>
                      </div>
                      <div style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-100)" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "4px" }}>User ID/Email</div>
                        <div style={{ fontSize: "14px" }}>{selectedAgentRequest.userId}</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <button onClick={() => handleApproveAgent(selectedAgentRequest)} className="btn-primary" style={{ justifyContent: "center", padding: "12px" }}>
                        <CheckCircle2 size={18} /> Approve Application
                      </button>

                      <div style={{ marginTop: "10px", borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px" }}>
                        <textarea
                          placeholder="Rejection reason (visible to user)..."
                          value={agentRejectComment}
                          onChange={(e) => setAgentRejectComment(e.target.value)}
                          className="input-field"
                          rows={3}
                        />
                        <button onClick={() => handleRejectAgent(selectedAgentRequest)} className="btn-danger" style={{ width: "100%", justifyContent: "center", padding: "12px" }}>
                          <XCircle size={18} /> Decline Application
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Investments Tab */}
          {activeTab === "investments" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ relative: "relative", flex: 1, maxWidth: "400px" }}>
                  <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search investments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px" }}
                  />
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>
                  Total: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allInvestments.length}</span>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedInvestment ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Investments List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {filteredInvestments.map(investment => (
                    <div
                      key={investment.id}
                      onClick={() => setSelectedInvestment(investment)}
                      style={{
                        padding: "16px 20px",
                        marginBottom: "8px",
                        borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedInvestment?.id === investment.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedInvestment?.id === investment.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                      onMouseEnter={e => !selectedInvestment?.id === investment.id && (e.currentTarget.style.borderColor = "var(--color-gray-200)")}
                      onMouseLeave={e => !selectedInvestment?.id === investment.id && (e.currentTarget.style.borderColor = "var(--color-gray-100)")}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "8px" }}>
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{investment.name || "N/A"}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)" }}>{investment.owner || "Unknown"}</div>
                        </div>
                        <div style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                          fontWeight: 700,
                          background: investment.investment_status === " active" ? "var(--color-success-light)" : "var(--color-danger-light)",
                          color: investment.investment_status === " active" ? "#15803d" : "#7f1d1d"
                        }}>
                          {investment.investment_status?.trim().toUpperCase() || "UNKNOWN"}
                        </div>
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--color-gray-600)", display: "flex", justifyContent: "space-between" }}>
                        <span>Value: <span style={{ fontWeight: 700 }}>₦{(investment.price || "0").toLocaleString()}</span></span>
                        <span>Qty: <span style={{ fontWeight: 700 }}>{investment.quantity || 0}</span></span>
                      </div>
                    </div>
                  ))}
                  {filteredInvestments.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-gray-400)" }}>
                      No investments found
                    </div>
                  )}
                </div>

                {/* Investment Details */}
                {selectedInvestment && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "var(--color-gray-900)" }}>
                      Investment Details
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Product Name</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.name || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Owner</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.owner || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Price Per Unit</div>
                        <div style={{ fontSize: "16px", fontWeight: 800, color: "var(--color-primary)" }}>₦{(selectedInvestment.price || "0").toLocaleString()}</div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div>
                          <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Quantity</div>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.quantity || 0}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Unit</div>
                          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.measurment || "N/A"}</div>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Date</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.date || "N/A"}</div>
                      </div>
                      <div style={{ position: "relative" }}>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Category</div>
                        {!editingInvestmentCategory ? (
                          <div
                            onClick={() => {
                              setEditingInvestmentCategory(true);
                              setInvestmentCategoryInput(selectedInvestment.category || "");
                            }}
                            style={{
                              fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)",
                              padding: "8px 12px", borderRadius: "6px", backgroundColor: "var(--color-gray-50)",
                              cursor: "pointer", border: "1px solid transparent", transition: "all 0.2s"
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = "var(--color-gray-100)";
                              e.currentTarget.style.borderColor = "var(--color-primary)";
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = "var(--color-gray-50)";
                              e.currentTarget.style.borderColor = "transparent";
                            }}>
                            {selectedInvestment.category ? selectedInvestment.category : "Click to add category"}
                          </div>
                        ) : (
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              value={investmentCategoryInput}
                              onChange={(e) => {
                                setInvestmentCategoryInput(e.target.value);
                                setShowCategorySuggestions(true);
                              }}
                              onFocus={() => setShowCategorySuggestions(true)}
                              onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                              placeholder="Type category name..."
                              style={{
                                width: "100%", padding: "8px 12px", borderRadius: "6px",
                                border: "1px solid var(--color-primary)", fontSize: "14px",
                                fontWeight: 500, boxSizing: "border-box"
                              }}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateInvestmentCategory(investmentCategoryInput);
                                } else if (e.key === "Escape") {
                                  setEditingInvestmentCategory(false);
                                  setInvestmentCategoryInput("");
                                }
                              }}
                            />
                            {showCategorySuggestions && investmentCategoryInput && (
                              <div style={{
                                position: "absolute", top: "100%", left: 0, right: 0,
                                backgroundColor: "white", border: "1px solid var(--color-gray-200)",
                                borderTop: "none", borderRadius: "0 0 6px 6px",
                                maxHeight: "200px", overflow: "auto", zIndex: 10
                              }}>
                                {allCategories
                                  .filter(cat => cat.name.toLowerCase().includes(investmentCategoryInput.toLowerCase()))
                                  .map(cat => (
                                    <div
                                      key={cat.id}
                                      onClick={() => {
                                        handleUpdateInvestmentCategory(cat.name);
                                      }}
                                      style={{
                                        padding: "10px 12px", cursor: "pointer",
                                        borderBottom: "1px solid var(--color-gray-100)",
                                        fontSize: "13px", color: "var(--color-gray-800)",
                                        transition: "background 0.15s"
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.backgroundColor = "var(--color-gray-50)"}
                                      onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                                    >
                                      {cat.name}
                                    </div>
                                  ))}
                                {allCategories.filter(cat => cat.name.toLowerCase().includes(investmentCategoryInput.toLowerCase())).length === 0 && (
                                  <div style={{ padding: "10px 12px", color: "var(--color-gray-400)", fontSize: "13px" }}>
                                    Press Enter to create: <strong>"{investmentCategoryInput}"</strong>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Date</div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-800)" }}>{selectedInvestment.date || "N/A"}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600, marginBottom: "4px" }}>Status</div>
                        <div style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background: selectedInvestment.investment_status === " active" ? "var(--color-success-light)" : "var(--color-danger-light)",
                          color: selectedInvestment.investment_status === " active" ? "#15803d" : "#7f1d1d"
                        }}>
                          {selectedInvestment.investment_status?.trim().toUpperCase() || "UNKNOWN"}
                        </div>
                      </div>

                      <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "16px" }}>
                        <button
                          onClick={handleDeleteInvestment}
                          style={{
                            width: "100%", padding: "10px", borderRadius: "var(--radius-md)",
                            fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: "var(--color-danger)", color: "white", border: "none"
                          }}>
                          <Trash2 size={14} style={{ marginRight: "6px", display: "inline" }} />
                          Delete Investment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ CENTRAL MARKET TAB ══ */}
          {activeTab === "central_market" && (
            <div style={{ padding: "clamp(16px, 4vw, 28px)" }}>
              <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "16px" }}>
                <div>
                  <h2 style={{ fontSize: "24px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "8px" }}>Central Market Requests</h2>
                  <p style={{ color: "var(--color-gray-500)", fontSize: "14px" }}>View and manage every active request posted on the Agrivestia Central Market.</p>
                </div>
                <div style={{ position: "relative", width: "100%", maxWidth: "320px" }}>
                  <Search size={16} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    placeholder="Search by product or buyer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-field"
                    style={{ marginBottom: 0, paddingLeft: "42px", height: "42px" }}
                  />
                </div>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))",
                gap: "24px"
              }}>
                {allMarketplaceLeads.filter(l =>
                  l.product?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.buyer?.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((lead) => (
                  <div key={lead.id} className="card" style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px", border: "1px solid var(--color-gray-100)", transition: "all 0.2s" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>{lead.product}</div>
                        <div style={{ fontSize: "18px", fontWeight: 900, color: "var(--color-gray-900)" }}>{lead.quantity}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteCentralMarketLead(lead.id)}
                        style={{ color: "var(--color-danger)", background: "rgba(220, 38, 38, 0.05)", border: "1px solid rgba(220, 38, 38, 0.1)", cursor: "pointer", padding: "8px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title="Delete Request"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--color-gray-700)" }}>
                        <User size={14} color="var(--color-gray-400)" /> <strong>Buyer:</strong> {lead.buyer}
                      </div>
                      {lead.price && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--color-gray-700)" }}>
                          <Tag size={14} color="var(--color-gray-400)" /> <strong>Price/Budget:</strong> {lead.price}
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: "13px", height: "64px", color: "var(--color-gray-500)", lineHeight: 1.6, background: "var(--color-gray-50)", padding: "12px", borderRadius: "12px", border: "1px solid var(--color-gray-100)", overflowY: "auto" }}>
                      {lead.description || "No description provided."}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "auto", fontSize: "12px", color: "var(--color-gray-400)", borderTop: "1px solid var(--color-gray-50)", paddingTop: "14px" }}>
                      <Clock size={14} /> Posted on {lead.time}
                      {lead.source === "ai_agent_search" ? (
                        <span style={{ marginLeft: "auto", background: "linear-gradient(135deg, #22c55e, #15803d)", color: "white", fontWeight: 800, fontSize: "9px", padding: "2px 8px", borderRadius: "20px", textTransform: "uppercase" }}>AI LEAD</span>
                      ) : (
                        <span style={{ marginLeft: "auto", background: "var(--color-gray-100)", color: "var(--color-gray-500)", fontWeight: 700, fontSize: "9px", padding: "2px 8px", borderRadius: "20px", textTransform: "uppercase" }}>ORGANIC</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {allMarketplaceLeads.length === 0 && (
                <div style={{ textAlign: "center", padding: "100px 20px" }}>
                  <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "var(--color-gray-50)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                    <Globe size={28} color="var(--color-gray-300)" />
                  </div>
                  <h3 style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-gray-700)", marginBottom: "8px" }}>No central market requests found</h3>
                  <p style={{ color: "var(--color-gray-400)", fontSize: "14px" }}>Requests posted on the marketplace will appear here.</p>
                </div>
              )}
            </div>
          )}

          {/* ══ FINANCES TAB ══ */}
          {activeTab === "finances" && (
            <div style={{ padding: "clamp(16px, 4vw, 28px)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 450px), 1fr))", gap: "28px" }}>

                {/* Platform Wallet & Payout */}
                <div className="card" style={{ padding: "28px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                    <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <TrendingUp size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-gray-900)" }}>Platform Wallet</h3>
                      <p style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>Manage accumulated platform commissions</p>
                    </div>
                  </div>

                  <div style={{ padding: "24px", background: "var(--color-gray-50)", borderRadius: "16px", border: "1px solid var(--color-gray-100)", marginBottom: "24px", textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "var(--color-gray-400)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "8px" }}>Current Balance</div>
                    <div style={{ fontSize: "32px", fontWeight: 900, color: "var(--color-primary-dark)" }}>₦{adminWallet.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}</div>
                  </div>

                  <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "24px" }}>
                    <label style={{ fontSize: "13px", fontWeight: 700, color: "var(--color-gray-700)", display: "block", marginBottom: "8px" }}>Process Payout</label>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <input
                        type="number"
                        placeholder="Amount to withdraw"
                        value={payoutAmount}
                        onChange={(e) => setPayoutAmount(e.target.value)}
                        className="input-field"
                        style={{ marginBottom: 0, flex: 1 }}
                      />
                      <button onClick={handleAdminPayout} className="btn-primary" style={{ padding: "0 24px" }}>Withdraw</button>
                    </div>
                    <p style={{ fontSize: "11px", color: "var(--color-gray-400)", marginTop: "10px" }}>* Funds will be deducted from the platform wallet immediately.</p>
                  </div>
                </div>

                {/* Fund Transfer Tool */}
                <div className="card" style={{ padding: "28px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                    <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <RefreshCw size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-gray-900)" }}>Fund Transfer Tool</h3>
                      <p style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>Move funds between any two system wallets</p>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* From Email */}
                    <div style={{ position: "relative" }}>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>From User Email</label>
                      <input
                        placeholder="e.g. admin@agrivestia.com or user@example.com"
                        value={transferForm.fromEmail}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTransferForm({ ...transferForm, fromEmail: val });
                          if (val.length > 1) {
                            setFromSuggestions(allUsers.filter(u => u.email?.toLowerCase().includes(val.toLowerCase())).slice(0, 5));
                          } else {
                            setFromSuggestions([]);
                          }
                        }}
                        className="input-field"
                        style={{ marginBottom: 0 }}
                        onBlur={() => setTimeout(() => setFromSuggestions([]), 200)}
                      />
                      {fromSuggestions.length > 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "white", border: "1px solid var(--color-gray-200)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)", maxHeight: "150px", overflow: "auto" }}>
                          {fromSuggestions.map(u => (
                            <div key={u.id} onClick={() => { setTransferForm({ ...transferForm, fromEmail: u.email }); setFromSuggestions([]); }} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--color-gray-50)" }}>
                              <div style={{ fontSize: "12px", fontWeight: 700 }}>{u.name}</div>
                              <div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>{u.email}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* To Email */}
                    <div style={{ position: "relative" }}>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>To User Email</label>
                      <input
                        placeholder="e.g. user@example.com"
                        value={transferForm.toEmail}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTransferForm({ ...transferForm, toEmail: val });
                          if (val.length > 1) {
                            setToSuggestions(allUsers.filter(u => u.email?.toLowerCase().includes(val.toLowerCase())).slice(0, 5));
                          } else {
                            setToSuggestions([]);
                          }
                        }}
                        className="input-field"
                        style={{ marginBottom: 0 }}
                        onBlur={() => setTimeout(() => setToSuggestions([]), 200)}
                      />
                      {toSuggestions.length > 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "white", border: "1px solid var(--color-gray-200)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)", maxHeight: "150px", overflow: "auto" }}>
                          {toSuggestions.map(u => (
                            <div key={u.id} onClick={() => { setTransferForm({ ...transferForm, toEmail: u.email }); setToSuggestions([]); }} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--color-gray-50)" }}>
                              <div style={{ fontSize: "12px", fontWeight: 700 }}>{u.name}</div>
                              <div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>{u.email}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Amount (₦)</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={transferForm.amount}
                        onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                        className="input-field"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <button onClick={handleUserToUserTransfer} className="btn-primary" style={{ marginTop: "8px", justifyContent: "center", padding: "14px" }}>
                      Execute Transfer
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Agents Management Tab */}
          {activeTab === "agents" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-600)" }}>
                  Verified Agents: <span style={{ color: "var(--color-primary)" }}>{allUsers.filter(u => u.isAgent).length}</span>
                </div>
              </div>

              {/* Reassignment Section */}
              <div className="card" style={{ padding: "28px", marginBottom: "28px", background: "var(--color-white)", border: "1px solid var(--color-gray-100)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                  <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <RefreshCw size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-gray-900)" }}>Bulk Client Migration</h3>
                    <p style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>Move all managed clients from one agent to another</p>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr)) 160px", gap: "16px", alignItems: "end" }}>
                  <div style={{ position: "relative" }}>
                    <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>From Agent Email</label>
                    <input
                      placeholder="Source Agent Email"
                      value={reassignForm.fromAgentEmail}
                      onChange={(e) => {
                        const val = e.target.value;
                        setReassignForm({ ...reassignForm, fromAgentEmail: val });
                        setFromAgentSug(val.length > 1 ? allUsers.filter(u => u.isAgent && u.email?.toLowerCase().includes(val.toLowerCase())).slice(0, 5) : []);
                      }}
                      className="input-field"
                      style={{ marginBottom: 0 }}
                      onBlur={() => setTimeout(() => setFromAgentSug([]), 200)}
                    />
                    {fromAgentSug.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "white", border: "1px solid var(--color-gray-200)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)", maxHeight: "150px", overflow: "auto" }}>
                        {fromAgentSug.map(u => (
                          <div key={u.id} onClick={() => { setReassignForm({ ...reassignForm, fromAgentEmail: u.email }); setFromAgentSug([]); }} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--color-gray-50)" }}>
                            <div style={{ fontSize: "12px", fontWeight: 700 }}>{u.name}</div>
                            <div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>{u.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ position: "relative" }}>
                    <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>To Agent Email</label>
                    <input
                      placeholder="Target Agent Email"
                      value={reassignForm.toAgentEmail}
                      onChange={(e) => {
                        const val = e.target.value;
                        setReassignForm({ ...reassignForm, toAgentEmail: val });
                        setToAgentSug(val.length > 1 ? allUsers.filter(u => u.isAgent && u.email?.toLowerCase().includes(val.toLowerCase())).slice(0, 5) : []);
                      }}
                      className="input-field"
                      style={{ marginBottom: 0 }}
                      onBlur={() => setTimeout(() => setToAgentSug([]), 200)}
                    />
                    {toAgentSug.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, background: "white", border: "1px solid var(--color-gray-200)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)", maxHeight: "150px", overflow: "auto" }}>
                        {toAgentSug.map(u => (
                          <div key={u.id} onClick={() => { setReassignForm({ ...reassignForm, toAgentEmail: u.email }); setToAgentSug([]); }} style={{ padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid var(--color-gray-50)" }}>
                            <div style={{ fontSize: "12px", fontWeight: 700 }}>{u.name}</div>
                            <div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>{u.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={handleReassignClients} className="btn-primary" style={{ padding: "14px", justifyContent: "center" }}>Migrate Clients</button>
                </div>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedAgent ? "1fr 1fr" : "1fr", gap: "24px" }}>
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {allUsers.filter(u => u.isAgent).map(agent => (
                    <div
                      key={agent.id}
                      onClick={() => setSelectedAgent(agent)}
                      style={{
                        padding: "16px 20px", marginBottom: "8px", borderRadius: "var(--radius-md)",
                        border: `2px solid ${selectedAgent?.id === agent.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedAgent?.id === agent.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer", transition: "all 0.2s"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)" }}>{agent.name}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)" }}>Agent ID: {agent.agentId || "N/A"}</div>
                        </div>
                        <div style={{
                          padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 700,
                          background: agent.agentStatus === "approved" ? "var(--color-success-light)" : "var(--color-danger-light)",
                          color: agent.agentStatus === "approved" ? "#15803d" : "#7f1d1d"
                        }}>
                          {agent.agentStatus?.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedAgent && (
                  <div className="card" style={{ padding: "24px", height: "fit-content" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                      <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "var(--color-primary)" }}>
                        {selectedAgent.name[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "16px" }}>{selectedAgent.name}</div>
                        <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>Verified Agrivestia Agent</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
                      {[["Agent ID", selectedAgent.agentId], ["Email", selectedAgent.email], ["Phone", selectedAgent.phone]].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase" }}>{l}</div>
                          <div style={{ fontSize: "14px" }}>{v || "N/A"}</div>
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase" }}>Status</div>
                        <div style={{
                          display: "inline-block", marginTop: "4px", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700,
                          background: selectedAgent.agentStatus === "approved" ? "var(--color-success-light)" : "var(--color-danger-light)",
                          color: selectedAgent.agentStatus === "approved" ? "#15803d" : "#7f1d1d"
                        }}>
                          {selectedAgent.agentStatus?.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleToggleAgentActive(selectedAgent, selectedAgent.agentStatus !== 'approved')}
                      style={{
                        width: "100%", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid",
                        fontSize: "14px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
                        borderColor: selectedAgent.agentStatus !== 'approved' ? "var(--color-success)" : "var(--color-danger)",
                        background: selectedAgent.agentStatus !== 'approved' ? "var(--color-success-light)" : "var(--color-danger-light)",
                        color: selectedAgent.agentStatus !== 'approved' ? "#15803d" : "#b91c1c"
                      }}>
                      {selectedAgent.agentStatus !== 'approved' ? "✓ Restore Agent Access" : "⊘ Disable Agent Access"}
                    </button>
                    {selectedAgent.agentStatus === 'approved' && (
                      <p style={{ fontSize: "11px", color: "var(--color-gray-400)", marginTop: "12px", textAlign: "center" }}>
                        Disabling agent access will hide all agent features from this user but keep their account active.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blog Posts Tab */}
          {activeTab === "blogs" && (
            <div>
              <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "14px", color: "var(--color-gray-500)" }}>
                  Total Blogs: <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{allBlogs.length}</span>
                </div>
                <button
                  onClick={() => { setIsEditingBlog(false); setBlogForm({ title: "", content: "", image: "", author: "Admin" }); setShowBlogModal(true); }}
                  className="btn-primary"
                  style={{ padding: "10px 20px" }}
                >
                  <Plus size={18} /> New Post
                </button>
              </div>

              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: selectedBlog ? "1fr 1fr" : "1fr", gap: "24px" }}>
                {/* Blog List */}
                <div style={{ maxHeight: "80vh", overflow: "auto" }}>
                  {allBlogs.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--color-gray-400)" }}>
                      <Sparkles size={36} style={{ margin: "0 auto 12px", opacity: 0.3 }} />
                      <div style={{ fontWeight: 600 }}>No blog posts yet</div>
                    </div>
                  ) : allBlogs.map(blog => (
                    <div
                      key={blog.id}
                      onClick={() => setSelectedBlog(blog)}
                      style={{
                        padding: "16px 20px", marginBottom: "8px", borderRadius: "12px",
                        border: `2px solid ${selectedBlog?.id === blog.id ? "var(--color-primary)" : "var(--color-gray-100)"}`,
                        background: selectedBlog?.id === blog.id ? "var(--color-primary-light)" : "var(--color-white)",
                        cursor: "pointer", transition: "all 0.2s"
                      }}
                    >
                      <div style={{ display: "flex", gap: "12px" }}>
                        {blog.image && (
                          <div style={{ width: "60px", height: "60px", borderRadius: "8px", overflow: "hidden", flexShrink: 0 }}>
                            <img src={blog.image} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-900)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{blog.title}</div>
                          <div style={{ fontSize: "12px", color: "var(--color-gray-500)", marginTop: "4px" }}>By {blog.author} &bull; {blog.timestamp?.toDate ? blog.timestamp.toDate().toLocaleDateString() : new Date(blog.timestamp).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Blog Detail */}
                {selectedBlog && (
                  <div className="card" style={{ padding: "24px", height: "fit-content", maxHeight: "85vh", overflowY: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "18px", fontWeight: 800 }}>Post Details</h3>
                      <button onClick={() => setSelectedBlog(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
                    </div>

                    {selectedBlog.image && (
                      <div style={{ marginBottom: "20px", borderRadius: "12px", overflow: "hidden", height: "200px", background: "var(--color-gray-100)" }}>
                        <img src={selectedBlog.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                    )}

                    <div style={{ marginBottom: "24px" }}>
                      <h1 style={{ fontSize: "22px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "12px" }}>{selectedBlog.title}</h1>
                      <div style={{ fontSize: "14px", color: "var(--color-gray-600)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{selectedBlog.content}</div>
                    </div>

                    <div style={{ display: "flex", gap: "10px", paddingBottom: "24px", borderBottom: "1px solid var(--color-gray-100)" }}>
                      <button
                        onClick={() => { setIsEditingBlog(true); setBlogForm({ title: selectedBlog.title, content: selectedBlog.content, image: selectedBlog.image, author: selectedBlog.author }); setShowBlogModal(true); }}
                        className="btn-ghost"
                        style={{ flex: 1, justifyContent: "center" }}
                      >
                        <Edit2 size={16} /> Edit Post
                      </button>
                      <button
                        onClick={() => handleDeleteBlog(selectedBlog.id)}
                        className="btn-ghost"
                        style={{ color: "var(--color-danger)", borderColor: "var(--color-danger)", flex: 1, justifyContent: "center" }}
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>

                    <div style={{ marginTop: "24px" }}>
                      <h4 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                        <MessageSquare size={16} /> Comments ({blogComments.length})
                      </h4>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {blogComments.length === 0 ? (
                          <div style={{ fontSize: "13px", color: "var(--color-gray-400)", fontStyle: "italic" }}>No comments yet.</div>
                        ) : blogComments.map(comment => (
                          <div key={comment.id} style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "8px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                              <span style={{ fontWeight: 700, fontSize: "13px" }}>{comment.userName}</span>
                              <button
                                onClick={async () => {
                                  if (!window.confirm("Delete this comment?")) return;
                                  try {
                                    await deleteDoc(doc(db, "blog_comments", comment.id));
                                    setBlogComments(blogComments.filter(c => c.id !== comment.id));
                                    showToast("success", "Deleted", "Comment removed");
                                  } catch { showToast("error", "Error", "Failed to delete comment"); }
                                }}
                                style={{ background: "none", border: "none", color: "var(--color-danger)", cursor: "pointer" }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div style={{ fontSize: "13px", color: "var(--color-gray-600)" }}>{comment.comment}</div>
                            <div style={{ fontSize: "10px", color: "var(--color-gray-400)", marginTop: "4px" }}>{comment.timestamp?.toDate ? comment.timestamp.toDate().toLocaleString() : ""}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ AI AGENT SEARCH TAB ══ */}
          {activeTab === "ai_agent" && (
            <div style={{ padding: "clamp(16px, 4vw, 28px)" }}>

              {/* Hero trigger panel */}
              <div style={{
                background: "linear-gradient(135deg, #0f172a 0%, #0d2b0d 60%, #0f2d0f 100%)",
                borderRadius: "16px", padding: "clamp(24px, 5vw, 40px)", marginBottom: "28px",
                position: "relative", overflow: "hidden"
              }}>
                <div style={{ position: "absolute", inset: 0, opacity: 0.04, backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "28px 28px" }} />
                <div style={{ position: "absolute", top: "-100px", right: "-60px", width: "320px", height: "320px", background: "radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)" }} />
                <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: "24px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "20px", flexWrap: "wrap" }}>
                    <div style={{ width: "54px", height: "54px", borderRadius: "14px", background: "linear-gradient(135deg, #22c55e, #15803d)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 14px rgba(34,197,94,0.35)" }}>
                      <Bot size={26} color="white" />
                    </div>
                    <div style={{ flex: 1, minWidth: "260px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "clamp(20px, 4vw, 24px)", fontWeight: 900, color: "white", letterSpacing: "-0.02em" }}>AI Agent Search</span>
                        <span style={{ fontSize: "10px", fontWeight: 700, background: "rgba(34,197,94,0.18)", color: "#4ade80", padding: "3px 10px", borderRadius: "20px", border: "1px solid rgba(34,197,94,0.3)" }}>GEMINI POWERED</span>
                      </div>
                      <div style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)", lineHeight: 1.7, maxWidth: "620px" }}>
                        Autonomously searches <strong style={{ color: "rgba(255,255,255,0.85)" }}>Twitter/X, Facebook, LinkedIn, Jiji, forums</strong> and agricultural groups for people posting <strong style={{ color: "rgba(255,255,255,0.85)" }}>today</strong> looking to buy farm produce. Review leads and push them to the marketplace with one click.
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                    <button
                      onClick={runAgentSearch}
                      disabled={agentLoading}
                      style={{
                        padding: "14px 28px", borderRadius: "10px", border: "none",
                        background: agentLoading ? "rgba(34,197,94,0.3)" : "linear-gradient(135deg, #22c55e, #15803d)",
                        color: "white", fontWeight: 700, fontSize: "15px",
                        cursor: agentLoading ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: "10px",
                        transition: "all 0.2s",
                        boxShadow: agentLoading ? "none" : "0 4px 12px rgba(34,197,94,0.25)",
                        flex: "none"
                      }}
                      onMouseEnter={(e) => !agentLoading && (e.currentTarget.style.transform = "translateY(-1px)")}
                      onMouseLeave={(e) => !agentLoading && (e.currentTarget.style.transform = "translateY(0)")}
                    >
                      {agentLoading ? (
                        <>Searching Web <RefreshCw size={18} style={{ animation: "agentSpin 1s linear infinite" }} /></>
                      ) : (
                        <>Run AI Search <Globe size={18} /></>
                      )}
                    </button>
                    {agentResults.length > 0 && (
                      <button
                        onClick={runAgentSearch}
                        disabled={agentLoading}
                        style={{
                          background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.2)",
                          padding: "14px 24px", borderRadius: "10px", fontWeight: 600, fontSize: "14px", display: "flex", alignItems: "center", gap: "8px",
                          cursor: "pointer"
                        }}
                      >
                        <RefreshCw size={16} /> Refresh
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Error banner */}
              {agentError && (
                <div style={{ display: "flex", gap: "12px", padding: "14px 18px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "10px", marginBottom: "20px" }}>
                  <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: "1px" }} />
                  <div style={{ fontSize: "14px", color: "#991b1b", lineHeight: 1.5 }}>{agentError}</div>
                </div>
              )}

              {/* Loading skeleton */}
              {agentLoading && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "16px" }}>
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="card" style={{ padding: "20px" }}>
                      <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                        <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "var(--color-gray-100)", flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ height: "14px", background: "var(--color-gray-100)", borderRadius: "4px", marginBottom: "8px", width: "55%" }} />
                          <div style={{ height: "10px", background: "var(--color-gray-100)", borderRadius: "4px", width: "35%" }} />
                        </div>
                      </div>
                      <div style={{ height: "11px", background: "var(--color-gray-100)", borderRadius: "4px", marginBottom: "8px" }} />
                      <div style={{ height: "11px", background: "var(--color-gray-100)", borderRadius: "4px", width: "70%", marginBottom: "16px" }} />
                      <div style={{ height: "70px", background: "var(--color-gray-50)", borderRadius: "8px", marginBottom: "16px" }} />
                      <div style={{ height: "38px", background: "var(--color-gray-100)", borderRadius: "8px" }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Results grid */}
              {!agentLoading && agentResults.length > 0 && (
                <div>
                  <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)" }}>
                      {agentResults.length} Buyer Lead{agentResults.length !== 1 ? "s" : ""} Found &mdash; {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" })}
                    </div>
                    <button onClick={runAgentSearch} style={{ background: "none", border: "1px solid var(--color-gray-200)", borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", color: "var(--color-gray-600)" }}>
                      <RefreshCw size={13} /> Refresh Search
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "16px" }}>
                    {agentResults.map((result) => {
                      const isPushed = pushedIds.has(result._id);
                      const platformPalette = {
                        Twitter: { bg: "#e7f3ff", color: "#1d9bf0", border: "#bfdbfe", label: "Twitter/X" },
                        X: { bg: "#f1f5f9", color: "#0f172a", border: "#e2e8f0", label: "X (Twitter)" },
                        Facebook: { bg: "#eff6ff", color: "#1877f2", border: "#bfdbfe", label: "Facebook" },
                        LinkedIn: { bg: "#f0f9ff", color: "#0a66c2", border: "#bae6fd", label: "LinkedIn" },
                        WhatsApp: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "WhatsApp" },
                        Forum: { bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff", label: "Forum" },
                        Jiji: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa", label: "Jiji" },
                        Tonaton: { bg: "#fefce8", color: "#ca8a04", border: "#fde68a", label: "Tonaton" },
                      };
                      const pal = platformPalette[result.platform] || { bg: "#f8fafc", color: "#475569", border: "#e2e8f0", label: result.platform || "Web" };
                      const contactIcon = result.contactType === "Phone" ? <Phone size={12} /> : result.contactType === "Email" ? <Mail size={12} /> : <User size={12} />;

                      return (
                        <div key={result._id} className="card" style={{
                          padding: "20px", position: "relative",
                          border: isPushed ? "2px solid #22c55e" : "1px solid var(--color-gray-100)",
                          transition: "all 0.2s", overflow: "hidden"
                        }}>
                          {isPushed && (
                            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #22c55e, #4ade80)" }} />
                          )}

                          {/* Header row: produce + platform badge */}
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "14px" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <ShoppingBag size={18} color="var(--color-primary)" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "15px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {result.produce || "Farm Produce"}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span style={{ fontSize: "10px", fontWeight: 700, background: pal.bg, color: pal.color, border: `1px solid ${pal.border}`, padding: "2px 8px", borderRadius: "20px" }}>
                                  {pal.label}
                                </span>
                                {isPushed && (
                                  <span style={{ fontSize: "10px", fontWeight: 700, background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: "20px" }}>
                                    ✓ Pushed
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Info rows */}
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", minWidth: "62px" }}>Qty</span>
                              <span style={{ fontSize: "13px", fontWeight: 600, color: result.quantity && result.quantity !== "Not specified" ? "var(--color-gray-800)" : "var(--color-gray-300)" }}>
                                {result.quantity && result.quantity !== "Not specified" ? result.quantity : "Not specified"}
                              </span>
                            </div>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", minWidth: "62px" }}>Contact</span>
                              <span style={{ fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", gap: "5px", color: result.contact && result.contact !== "Not found" ? "var(--color-primary)" : "var(--color-gray-300)" }}>
                                {result.contact && result.contact !== "Not found" ? <>{contactIcon} {result.contact}</> : "Not found"}
                              </span>
                            </div>
                            {result.location && result.location !== "Not specified" && (
                              <div style={{ display: "flex", gap: "8px" }}>
                                <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", minWidth: "62px" }}>Location</span>
                                <span style={{ fontSize: "13px", color: "var(--color-gray-700)" }}>{result.location}</span>
                              </div>
                            )}
                          </div>

                          {/* Post snippet */}
                          {result.postSnippet && (
                            <div style={{ padding: "10px 12px", background: "var(--color-gray-50)", borderRadius: "8px", marginBottom: "14px", borderLeft: "3px solid var(--color-primary)" }}>
                              <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--color-gray-400)", marginBottom: "5px" }}>ORIGINAL POST</div>
                              <div style={{ fontSize: "12px", color: "var(--color-gray-700)", lineHeight: 1.6, fontStyle: "italic" }}>
                                "{result.postSnippet.length > 150 ? result.postSnippet.substring(0, 150) + "…" : result.postSnippet}"
                              </div>
                            </div>
                          )}

                          {/* Editable fields */}
                          <div style={{ marginBottom: "16px", background: "#f8fafc", padding: "12px", borderRadius: "10px", border: "1px dashed var(--color-gray-200)" }}>
                            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", marginBottom: "10px", display: "flex", alignItems: "center", gap: "5px" }}>
                              <Edit2 size={12} /> Customize for Marketplace
                            </div>

                            <div style={{ marginBottom: "10px" }}>
                              <div style={{ fontSize: "10px", color: "var(--color-gray-400)", marginBottom: "4px" }}>PRICE (₦)</div>
                              <input
                                type="text"
                                placeholder="Enter unit price (e.g. 5000)"
                                value={cardEdits[result._id]?.price || ""}
                                onChange={(e) => setCardEdits(prev => ({
                                  ...prev,
                                  [result._id]: { ...prev[result._id], price: e.target.value }
                                }))}
                                style={{
                                  width: "100%", padding: "8px 10px", borderRadius: "6px",
                                  border: "1px solid var(--color-gray-200)", fontSize: "13px",
                                  outline: "none", transition: "border-color 0.2s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "var(--color-primary)"}
                                onBlur={(e) => e.target.style.borderColor = "var(--color-gray-200)"}
                              />
                            </div>

                            <div>
                              <div style={{ fontSize: "10px", color: "var(--color-gray-400)", marginBottom: "4px" }}>DESCRIPTION</div>
                              <textarea
                                placeholder="Enter custom description..."
                                value={cardEdits[result._id]?.description || `[AI Agent Lead | ${result.platform || "Web"} | ${result.location && result.location !== "Not specified" ? result.location : "Nigeria"}] ${result.postSnippet || ""}`}
                                onChange={(e) => setCardEdits(prev => ({
                                  ...prev,
                                  [result._id]: { ...prev[result._id], description: e.target.value }
                                }))}
                                style={{
                                  width: "100%", padding: "8px 10px", borderRadius: "6px",
                                  border: "1px solid var(--color-gray-200)", fontSize: "13px",
                                  outline: "none", minHeight: "60px", resize: "vertical",
                                  fontFamily: "inherit", transition: "border-color 0.2s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "var(--color-primary)"}
                                onBlur={(e) => e.target.style.borderColor = "var(--color-gray-200)"}
                              />
                            </div>
                          </div>

                          {/* Action buttons */}
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={() => !isPushed && pushToMarketplace(result)}
                              disabled={isPushed}
                              style={{
                                flex: 1, padding: "10px 12px", border: "none", borderRadius: "8px",
                                fontWeight: 700, fontSize: "13px",
                                cursor: isPushed ? "default" : "pointer",
                                background: isPushed ? "#f0fdf4" : "linear-gradient(135deg, var(--color-primary), var(--color-primary-dark, #15803d))",
                                color: isPushed ? "#16a34a" : "white",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                                transition: "all 0.2s",
                                boxShadow: isPushed ? "none" : "0 2px 8px rgba(0,128,0,0.2)"
                              }}
                            >
                              {isPushed
                                ? <><CheckCircle2 size={14} /> Pushed to Market</>
                                : <><ShoppingBag size={14} /> Push to Marketplace</>}
                            </button>
                            {result.postUrl && (
                              <a
                                href={result.postUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="View original post"
                                style={{ width: "38px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--color-gray-200)", borderRadius: "8px", color: "var(--color-gray-500)", textDecoration: "none", flexShrink: 0 }}
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty / pre-search state */}
              {!agentLoading && agentResults.length === 0 && !agentError && agentSearchCount === 0 && (
                <div style={{ textAlign: "center", padding: "80px 20px" }}>
                  <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "var(--color-gray-50)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                    <Bot size={36} color="var(--color-gray-300)" />
                  </div>
                  <div style={{ fontSize: "17px", fontWeight: 700, color: "var(--color-gray-600)", marginBottom: "8px" }}>Ready to search the web</div>
                  <div style={{ fontSize: "14px", color: "var(--color-gray-400)", lineHeight: 1.7, maxWidth: "420px", margin: "0 auto" }}>
                    Click <strong>Run AI Search</strong> above to find people posting farm produce purchase requests on social media today.
                  </div>
                </div>
              )}

              <style>{`
                @keyframes agentSpin { to { transform: rotate(360deg); } }
                @keyframes fadeInUp {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                
                @media (max-width: 992px) {
                  .sidebar { transform: translateX(-100%); }
                  .sidebar.open { transform: translateX(0); }
                  .main-content { margin-left: 0 !important; }
                  .show-mobile { display: block !important; }
                  .hide-mobile { display: none !important; }
                }
              `}</style>
            </div>
          )}
        </div>
      </main>

      {/* Blog Post Modal */}
      {showBlogModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div className="card" style={{ width: "100%", maxWidth: "600px", padding: "32px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 800 }}>{isEditingBlog ? "Edit Blog Post" : "Create New Post"}</h2>
              <button onClick={() => setShowBlogModal(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={24} /></button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Post Title</label>
                <input
                  className="input-field"
                  placeholder="Enter a catchy title..."
                  value={blogForm.title}
                  onChange={e => setBlogForm({ ...blogForm, title: e.target.value })}
                  style={{ marginBottom: 0 }}
                />
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Cover Image</label>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="blog-image-upload"
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="blog-image-upload"
                    style={{
                      flex: 1, padding: "12px", border: "2px dashed var(--color-gray-200)",
                      borderRadius: "10px", textAlign: "center", cursor: "pointer",
                      fontSize: "13px", color: "var(--color-gray-500)", transition: "all 0.2s"
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "var(--color-primary)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--color-gray-200)"}
                  >
                    {isUploading ? `Uploading... ${uploadProgress}%` : "Click to upload image"}
                  </label>
                </div>
                {isUploading && (
                  <div style={{ width: "100%", height: "4px", background: "var(--color-gray-100)", borderRadius: "2px", marginBottom: "12px", overflow: "hidden" }}>
                    <div style={{ width: `${uploadProgress}%`, height: "100%", background: "var(--color-primary)", transition: "width 0.3s" }} />
                  </div>
                )}
                <input
                  className="input-field"
                  placeholder="Or paste image URL here..."
                  value={blogForm.image}
                  onChange={e => setBlogForm({ ...blogForm, image: e.target.value })}
                  style={{ marginBottom: 0 }}
                />
                {blogForm.image && (
                  <div style={{ marginTop: "12px", borderRadius: "10px", overflow: "hidden", height: "120px", border: "1px solid var(--color-gray-100)" }}>
                    <img src={blogForm.image} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                )}
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Author Name</label>
                <input
                  className="input-field"
                  placeholder="Admin"
                  value={blogForm.author}
                  onChange={e => setBlogForm({ ...blogForm, author: e.target.value })}
                  style={{ marginBottom: 0 }}
                />
              </div>

              <div>
                <label style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Content</label>
                <textarea
                  className="input-field"
                  placeholder="Write your article here..."
                  rows={8}
                  value={blogForm.content}
                  onChange={e => setBlogForm({ ...blogForm, content: e.target.value })}
                  style={{ marginBottom: 0, resize: "vertical", minHeight: "200px" }}
                />
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "12px" }}>
                <button
                  onClick={() => setShowBlogModal(false)}
                  className="btn-ghost"
                  style={{ flex: 1, justifyContent: "center" }}
                >
                  Cancel
                </button>
                <button
                  onClick={isEditingBlog ? handleUpdateBlog : handleCreateBlog}
                  className="btn-primary"
                  style={{ flex: 2, justifyContent: "center" }}
                >
                  {isEditingBlog ? "Save Changes" : "Publish Post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
